// src/pages/SecondLifeBestand.jsx
// Second-Life-Markt — die Bestandsuebersicht.
//
// Alle Nicht-A-Artikel aus LSM (B/C/DEF), eine Zeile je Artikel × Stufe, mit
// Live-Bestand aus Shopify, dem Kladdenzustand, dem **Rabatt** und der
// Beschreibungspflege.
// Backend: GET /api/second-life/bestand, POST /api/second-life/<id>/rabatt,
// POST /api/second-life/<id>/beschreibung.
// Vertrag: docs/superpowers/plans/2026-10-08-second-life-bestandstabelle.md.
//
// 🔴 Vier Dinge, die diese Seite bewusst so macht:
//
// 1. Die Sortier- und Filterregeln stehen NICHT hier, sondern in
//    `secondLife/bestand.js` — reine Funktionen, ohne DOM pruefbar.
//
// 2. DEF ist sichtbar und gesperrt. Kein Knopf ist aktiv, und der Grund steht
//    als SICHTBARER Text unter dem Namen — nicht in einem `title`. Ein gesperrter
//    MUI-Knopf hat `pointer-events: none` und `tabIndex: -1`; ein Tooltip kaeme
//    weder per Maus noch per Tastatur noch per Touch an (Regel-16-Blocker B3 in
//    Teil 1). Dieselbe Form wie im Karussell darueber (`secondLife/OpenCases.jsx`).
//
// 3. Unbekannter Bestand ist ein Geviertstrich, nie eine 0. `null` heisst
//    „unbekannt" — eine 0 waere eine Behauptung ueber den Lagerbestand.
//
// 4. 🔴 **Der Rabatt gehoert auf diese Seite** (Grill-Blocker B1, 2026-10-09).
//    `lege_listing_an` bricht VOR jedem Shopify-Schritt ab, wenn der Kladde der
//    bestaetigte Rabatt fehlt (`second_life.py:994-995` → 409 `kein_rabatt`),
//    und `approve` rechnet den Preis daraus. Das Karussell ist der einzige Ort
//    mit „Create" — ohne Rabatt endete dort fast jeder Klick (auf prod sind
//    **12 von 13** Kladdenzeilen `discovered`) in einer Absage, deren Satz auf
//    dieser Seite kein Ziel hatte. Das Bauteil ist `secondLife/RabattBlock.jsx`,
//    im aufgeklappten Editor der Zeile — nicht in jeder Zeile.
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PageContainer from '../components/common/PageContainer';
import Panel from '../components/common/Panel';
import ViewHeader from '../components/ViewHeader';
import ProductThumb from '../components/common/ProductThumb';
import Pill from '../components/common/Pill';
import EmptyState from '../components/common/EmptyState';
import TableFooterSimple from '../components/common/TableFooterSimple';
import TableViewport from '../components/common/TableViewport';
import ListToolbar from '../components/common/ListToolbar';
import BottomSheetMultiSelect from '../components/common/BottomSheetMultiSelect';
import FilterPillRow from '../components/common/FilterPillRow';
import RefreshButton from '../components/common/RefreshButton';
import RowActionsMenu from '../components/common/RowActionsMenu';
import MobileRecordCard from '../components/common/MobileRecordCard';
import useContainerWidth from '../components/common/useContainerWidth';
import useColumnPriority from '../components/common/useColumnPriority';
// 🔴 **Die Hausform der Live-Region, nicht ein neuer Bauteil** (2026-10-16,
// Luecke 1). `ProgressStatus` ist genau fuer Aufrufer exportiert, die die Region
// selbst setzen (`ProgressButton.jsx:83-86`, `statusRegion={false}`) — der
// `SplitButton`-Fall. Sie ist hier nicht als Knopfkind erlaubt und auch nicht
// innerhalb des Abschnitts der offenen Liste (Begruendung an der Verwendung).
import { ProgressStatus } from '../components/common/ProgressButton';
import useListQuery from '../components/common/listQuery/useListQuery';
import {
  approveSecondLife,
  bestandLesen,
  beschreibungSchreiben,
  beschreibungVorschau,
  createSecondLife,
  discardSecondLife,
  kladdeAnlegen,
} from '../api/secondLife';
import {
  BEDIENBREITE, BILD_SPALTE_PX, COLUMN_MAX, CONTENT_WIDTH, FS, PANEL_RADIUS, SP,
} from '../theme/tokens';
// 🔴 Der Warnton fuer SCHRIFT aus `v2Tone` (`tokens.js` `warnText`) — nicht
// `palette.warning.main`, das auf dem hellen Papier 3,113:1 liefert
// (`theme.js:1321`). Dieselbe Hausform traegt die offene Liste (`OpenCases.jsx`).
import { v2Tone } from '../theme/theme';
import { lsmArticleSearchUrl } from '../rma/lib/lsmUrls';
import { sanitizeEmailHtml } from '../rma/timeline/sanitizeEmailHtml';
import {
  AUSSCHUSS_GRUND,
  BESCHREIBUNG_LABEL,
  DEF,
  KEIN_AGGREGAT_GRUND,
  OHNE_PRODUKT_GRUND,
  STUFE_TON,
  ZUSTAND_TON,
  BESTAND_AUSFALL_HINWEIS,
  LEERE_BESTANDSSPALTE_HINWEIS,
  LSM_AUSFALL_HINWEIS,
  STUFEN,
  ZUSTAENDE,
  ZUSTAND_LABEL,
  bestandHerkunft,
  bestandQuelleLeer,
  bestandText,
  beschreibungGrund,
  euroText,
  filtere,
  laufendeAnsage,
  maxSeite,
  IDENTITY_OBERGRENZE_PX,
  mitBestand,
  quelleText,
  rabattMoeglich,
  rabattText,
  seiteVon,
  shopifyProduktUrl,
  sortiere,
  stempelText,
  stempelZusatz,
  vergleichspreisText,
  zeilenSchluessel,
} from './secondLife/bestand';
import Vorschlagsteil from './secondLife/Vorschlagsteil';
import RabattBlock from './secondLife/RabattBlock';
import { serverSatz } from './secondLife/fehler';
import { offeneFaelle } from './secondLife/offeneFaelleAuswahl';
import { textKurz } from './secondLife/schritte';

// 🔴 `STUFE_TON` und `ZUSTAND_TON` stehen seit dem 2026-10-09 in `bestand.js`
// — dieselbe Tabelle trug das Karussell zeichengleich ein zweites Mal
// (Regel-16-Bedingung 5).

// Der Zustand der Beschreibung. „geaendert" ist ein Hinweis, kein Fehler.
const BESCHR_TON = { fehlt: 'plain', vorschlag: 'info', geaendert: 'warn' };

// ── Die zwei benannten Grenzen der Breite (§4.1, S1, 2026-11-03) ────────────
// 🔴 `identity` wächst mit dem Überschuss, aber NUR bis `IDENTITY_OBERGRENZE_PX`
// — die Konstante steht seit R15-N4 (2026-11-03) in `bestand.js`, damit die
// Playwright-Probe PW7b sie importieren kann statt 480 abzuschreiben.
// 🔴 Wer den REST des Überschusses trägt. `'beschreibung'` ist die Bedingung des
// Entscheids: der Überschuss geht an INHALT, und die Description-Zelle zeigt
// ihn (Pille + zwei Zeilen des Entwurfs). Rückfall (Vertrag ändert sich):
// `ELASTISCHE_SPALTE = null` **und** `IDENTITY_OBERGRENZE_PX = Infinity`.
const ELASTISCHE_SPALTE = 'beschreibung';
// Wie viele Zeilen der Entwurf in der Zelle zeigt (N9). Kann die Trennlinie der
// klebenden Bildspalte bei gemischten Zeilen nicht gehalten werden, wird sie 1.
const BESCHREIBUNG_VORSCHAU_ZEILEN = 2;

// 🔴 **Die Anzeige-Seitenweise** (Regel-16-W3 der Runde 4). Die Route deckelt
// NICHT — der Spec-Entscheid „kein LIMIT" meint genau das und bleibt
// unberuehrt (`api/secondLife.js:63-67`). Auf prod sind es **777 Zeilen**: rund
// 7.000 Tabellenzellen gleichzeitig im DOM, ohne Fensterung und ohne `memo`, und
// die Suche baute bei JEDEM Tastendruck ein neues `sichtbar`-Array. Eine
// ANZEIGE-Seitenweise widerspricht dem Spec-Entscheid nicht: der Server liefert
// weiter alles, die Seite rendert 50 davon. Hausform: `TableFooterSimple`
// (`components/common/TableFooterSimple.jsx`, Vorgabe 50 Zeilen/Seite) — in 16
// Dateien gefuehrt, u. a. `ProductsTable.jsx`, `POInbox.jsx`.
const ROWS_PER_PAGE = 50;

/** Das Breitenmuster dieser Seite — EINE Quelle für Deckel und Meldung (§2.6).
 *
 *  🔴 `full`: die Seite rendert `<PageContainer width={MUSTER}>`
 *  (`inhaltsbreite.md:301`: `/second-life` | WF3 | `full`). Bis G3b rendert sie
 *  **ohne** `width` und trug damit das Vorgabemuster `reading` (1280) — sichtbar
 *  wurde das erst ab 1920, wo die Seite die Breite nicht nutzte.
 *
 *  🔴 Die Konstante steht hier, weil die zwei Verbraucher NICHT denselben Kontext
 *  sehen: `PageContainer` setzt den `PageWidthContext` erst um seine KINDER, und
 *  `SecondLifeBestand` ist sein ELTERNTEIL — ein `useContext(PageWidthContext)`
 *  hier läse den ÄUSSEREN Wert (`'reading'`), nicht den eigenen. Zwei Quellen für
 *  dasselbe Muster wären genau die Doppelquelle, gegen die G0b.2 angetreten ist;
 *  deshalb EIN Literal an EINER Stelle. 🔴 Hausform ist der Zweig
 *  `feat/ui2-t10-g2b-mobil-breiten` (`POOverview.jsx`); auf DIESER Basis ist er
 *  nicht gemergt, und `POOverview.jsx` traegt die Mechanik nicht (Regel-16-
 *  Warnung W3). Die auf dieser Basis gueltige Hausform ist `PageContainer`
 *  selbst (`width`-Prop) bzw. `ListenMuster.jsx`.
 *
 *  ⚠️ **Der Skelett zieht OHNE eigene Zeile mit.** §2.6 verlangt „der Skelett
 *  trägt das Muster der SEITE" — bei `/overview` war das eine eigene Datei
 *  (`POOverviewSkeleton.jsx`, eigener `MUSTER`-Wert, den PW14 vergleicht). Hier
 *  liegt der Skelett-Zweig IM selben `PageContainer` (`zeilen === null && !fehler`,
 *  unten) und erbt das Muster damit von selbst; eine zweite Konstante wäre eine
 *  zweite Quelle für dieselbe Aussage. */
const MUSTER = 'full';

/** Die Bildspalte — eine FESTE Breite, kein Eintrag im Spaltenbudget (§2.6).
 *
 *  🔴 **Warum sie nicht in `SPALTEN` steht, und warum das keine Auslassung ist.**
 *  §2.6 führt „Image | `kern` | 40-px-Bild" in seiner Tabelle — und rechnet im
 *  selben Absatz: *„Container 904 px → nur `kern` (Image **40** + Item + Level +
 *  Stock + Shopify + Actions 140)"*. Der Plan behandelt die Bildspalte dort also
 *  selbst als **40 px fest**, nicht als `COLUMN_MAX`-Spalte. Und er kann es gar
 *  nicht anders meinen: die kleinste Spaltenart ist `number` (72–160), und
 *  „Menge, Betrag, Prozentsatz" (`tokens.js:2262`) ist kein Deskriptor für ein
 *  40-px-Vorschaubild — eine `kind`-Zuordnung wäre eine falsche Aussage über die
 *  Art, nur damit das Feld gefüllt ist. Sie steht deshalb NEBEN dem Budget, genau
 *  wie die ⋮-Spalte (§2.6, G2b) — und ist `kern` im Sinne der Sichtbarkeit: sie
 *  wird NIE ausgeblendet.
 *
 *  Die Zahl: 40 px Bild (`ProductThumb size={40}`, wie die Bestandszeile) + 2 ×
 *  8 px Zellpolster (`SP.tight`) = 56. 🔴 **Das Polster wird an der Zelle
 *  AUSDRÜCKLICH gesetzt** (`px: SP.tight`), und das ist keine Kosmetik: MUI
 *  `size="small"` trägt horizontal **16 px** je Seite (das Theme überschreibt
 *  nur `paddingTop`/`paddingBottom`, `theme.js:540-541`). Mit 16 px läge das
 *  40-px-Bild in einem 24-px-Inhaltskasten und ragte in das Polster — die
 *  Begründung „2 × 8 px" wäre dann falsch (Regel-15-Warnung W3). Der Wert steht
 *  als Token in `tokens.js` (`BILD_SPALTE_PX`); `width: <px>` an einer
 *  `TableCell` ist die Hausform (`PODetail.jsx:4148` `width: 48`), und
 *  `check_ui_tokens.py` führt `width` in keiner seiner sechs Kategorien.
 *
 *  🔴 **Der Wert ist seit G3b-Nachbesserung 3 ein Token** (`tokens.js`
 *  `BILD_SPALTE_PX`, neben `BEDIENBREITE`): die Bestandsliste, die Warteschlange
 *  und die Playwright-Probe lesen ihn aus EINER Quelle (R15 W3). */

/** Die SECHS Datenspalten in DOM-Reihenfolge (§2.6) — ohne Bild und ohne ⋮.
 *
 *  `priority`/`kind` sind die Eingaben von `useColumnPriority` (welche Spalte bei
 *  welcher Containerbreite weicht) und `COLUMN_MAX` (Ober-/Untergrenze der Zelle).
 *  🔴 **Kein eigenes `max` am Deskriptor** — der Baustein leitet `min/max` aus
 *  `COLUMN_MAX[kind]` ab (Grill W12); zwei Quellen für dieselbe Zahl sind genau
 *  der Zustand, den das Konzept abschafft.
 *
 *  🔴 **Jede Spalte braucht BEIDES — Stufe UND Art** (Grill W8, §2.6): der
 *  Meldekanal rechnet das Budget als `Σ COLUMN_MAX[kind].max` über die SICHTBAREN
 *  Spalten (`TableViewport.jsx:274-277`) und meldet nur bei `tabellenBreite > 0`.
 *  Ohne `kind` wäre `COLUMN_MAX[kind]` `undefined` → `?? 0` → Budget 0 → keine
 *  Meldung → kein `searchMax` am Kopf.
 *
 *  🔴 **Startwerte, nicht gemessen** (§3.2.2): G0b hat gemessen; weicht die
 *  Messung ab, folgt die Zahl der Messung. `kern` = immer sichtbar, `lg`/`xl`
 *  füllen nach Rang, solange das Budget unter dem Behälter liegt (`waehleSichtbare`).
 *
 *  | Spalte | Stufe | Art | warum |
 *  |---|---|---|---|
 *  | Item | `kern` | `identity` | die Kennung (SKU + Name) — sie ist die ZWEITE Spalte; kleben tut die BILDSPALTE (`stickyFirstColumn`, s. den `TableViewport`-Kommentar unten) |
 *  | Level | `kern` | `chip` | ein `Pill` (B/C/DEF) |
 *  | Stock | `kern` | `number` | „die EINE Zahl, wegen der man die Liste öffnet" (§3.2.2) |
 *  | Shopify | `kern` | `chip` | Status — §9.2 N9.8: nie über Farbe allein, aber IMMER sichtbar |
 *  | Price | `lg` | `number` | die zweite Zahl |
 *  | Description | `xl` | `chip` | Nebenwert (ein `Pill`) |
 *
 *  ⚠️ **Description ist ein `chip`, kein `text`** — die Zelle trägt ein `Pill`
 *  („Missing"/„Suggestion"/„Changed"), keine Fließtextspalte. Die Art beschreibt
 *  den INHALT der Zelle, nicht ihre Breite; `chip` (88–200) trifft die Form.
 *
 *  ⚠️ **Die ⋮-Spalte steht nicht hier** — sie trägt keine Art, sondern
 *  `BEDIENBREITE.aktion` (140, §2.6); sie ist KEINE Datenspalte. */
const SPALTEN = [
  { key: 'item', priority: 'kern', kind: 'identity' },
  { key: 'level', priority: 'kern', kind: 'chip' },
  { key: 'stock', priority: 'kern', kind: 'number' },
  { key: 'shopify', priority: 'kern', kind: 'chip' },
  { key: 'price', priority: 'lg', kind: 'number' },
  { key: 'beschreibung', priority: 'xl', kind: 'chip' },
];

/** `SPALTEN` nach Schlüssel — für die Breite einer einzelnen Kopf-/Datenzelle. */
const SPALTE_VON = Object.fromEntries(SPALTEN.map((s) => [s.key, s]));

// 🔴 **EIN Roller — und es ist die SEITE** (Weg (c), 2026-10-14). Owner,
// 2026-10-12 an der LIVE-Ansicht: „Tabelle scrollt doppelt — in sich und mit der
// Seite, das ist Quatsch." Bis hierher trug die Ansicht eine FESTE Hoehe
// (`SEITEN_HOEHE`, dazu `height: 100%`) und der Tabellenbereich darin einen
// Boden (`TABELLE_MINDESTHOEHE`) — die Tabelle rollte in sich, und auf niedrigen
// Schirmen rollte die Seite ZUSAETZLICH (gemessen: Nullpunkt H ≈ 932).
//
// Die Fesseln sind weg: ohne feste Hoehe waechst die Ansicht mit ihrem Inhalt,
// die SEITE wird der eine Roller, und der Tabellenkasten hat keine eigene Hoehe
// mehr — `flexGrow: 1` (aus `TableViewport`) ist ohne freien Platz wirkungslos,
// `overflow: auto` erzeugt dann keinen inneren Vertikalroller mehr.
//
// ⚠️ **Was das kostet, benannt statt verschwiegen:** auf hohen Schirmen rollt
// die Seite, und mit ihr die Liste und die Filterleiste. Dafuer klebt der
// Tabellenkopf ab `lg` an der Seite (`top: 64`, Hausform
// `RMACasesDashboardBody.jsx` › `TABELLE_SX`) und der Fuss am Unterrand
// (`TableFooterSimple`, `position: sticky` ab `sm`) — beides wird durch Weg (c)
// erst WIRKSAM. Die Messung samt Viewports steht im Plan
// `docs/superpowers/plans/2026-10-13-second-life-offene-faelle.md` §2.2/§2.4.
//
// ⚠️ **Zwei Konstanten sind hier gestrichen** (`SEITEN_HOEHE`,
// `TABELLE_MINDESTHOEHE`) — sie waren die zwei Enden derselben Fessel. Wer eine
// davon zurueckholt, holt den doppelten Roller zurueck.

// 🔴 **Der Listen-Zustand liegt ab G3a in der ADRESSE** (`useListQuery`, UI2 Teil
// 10). Die sechs Parameter der Karte (§3.1) — Suche `q`, Facette `level`, Facette
// `shopify`, Quick-Filter `quick`, Sortierung `sort`, Seite `page` — stehen in der
// URL, nicht mehr in sechs `useState`. Der Grund ist der Bestand: ein geteilter
// Link trug bisher NICHTS vom Filterzustand, und ein Neuladen warf ihn weg.
//
// 🔴 **Die Vorgabe ist KEINE** (§2.4, am Kode gemessen): kein Wert engt ein, die
// Liste zeigt im Grundzustand den vollen Bestand. Deshalb hat keine Dimension ein
// `default`, es gibt keinen Vorgabe-Chip in Z3, und „N of M" hat im Grundzustand
// M = N. P28 ist damit NICHT im Umfang (§5.3).
//
// 🔴 Die Facetten-`values` sind die FESTE Werte-Liste (`STUFEN`/`ZUSTAENDE`), nicht
// die beobachtete Menge (§4): eine datenabhängige Liste wechselte mit jeder
// Änderung, und wer „C" sucht, fände weder Wert noch Begründung. Eine Wahl ohne
// Treffer führt in den ERKLÄRTEN `no-match`-Zustand (§2.8, P9).
//
// ⚠️ **Der frühere 250-ms-Entpreller entfällt** (§1.2): `SearchTrigger` bringt
// seine eigene Entprellung mit, und `useListQuery` ist der EINE Schreiber.
const DIMENSIONEN = [
  // Freitext (keine `values`): nur so entsteht der Chip „Search: <Begriff>" in Z3
  // (`useListQuery.js`, `baueChips`) — einen zweiten Kanal gibt es nicht.
  { key: 'q', kind: 'single', label: 'Search' },
  // Facette, multi. Die Optionen tragen KEINEN Zähler (§4/§13 Abweichung 11:
  // `BottomSheetMultiSelect` kennt kein `count`, und G3 ändert keinen Baustein).
  { key: 'level', kind: 'multi', label: 'Level', values: STUFEN },
  {
    key: 'shopify',
    kind: 'multi',
    label: 'Shopify',
    values: ZUSTAENDE.map((z) => ({ value: z, label: ZUSTAND_LABEL[z] || z })),
  },
  // Quick-Filter „In stock" (§3.1 Ziff. 4) — der Zähler an der Pille ist der
  // BESTAND, nicht die gefilterte Menge (§4, Owner-Entscheid OE-G3-1).
  //
  // 🔴 **`{ value, label }`, nicht `['stock']`** (Nachbesserung 1, G3a). Der
  // Schlüssel in der Adresse heisst `stock`, die Pille heisst „In stock" — und
  // der Chip in Z3 muss die Pille WIEDERGEBEN, nicht den Schlüssel: `baueChips`
  // baut sein Etikett über `etikett(d, v)` (`useListQuery.js:307`), und das
  // liefert bei einem blanken String den ROHWERT. Mit `['stock']` stand in Z3
  // „✕ Remove filter stock", waehrend die gedrueckte Pille „In stock" heisst —
  // `messeP6` verlangt, dass der Chip den Namen der Pille ENTHAELT, und die
  // Probe fiel mit `gedrueckt: ["In stock"] · chips: ["Remove filter stock"]`.
  // Die Objektform ist die der Nachbardimension `shopify` (`:191`) — dieselbe
  // Hausform, nur diesmal auch fuer den Quick-Filter.
  { key: 'quick', kind: 'quick', label: 'Stock',
    values: [{ value: 'stock', label: 'In stock' }] },
];

// Die zwei sortierbaren Spalten (§1.1): `feld` aufsteigend, `-feld` absteigend;
// die Vorgabe ist `-stock` (wie bisher `:706-707`).
const SORT_VALUES = ['name', 'stock', '-name', '-stock'];
const SORT_VORGABE = '-stock';

// Eine Modulkonstante, kein `[]`-Literal je Render: ein frisches Array liesse
// jeden `useMemo`, der es in den Abhängigkeiten führt, bei JEDEM Render neu
// rechnen (Hausform `ProductsTable.jsx`, `LEER_ARRAY`).
const LEER_LISTE = [];

// Der Shopify-Absprung je Zeile steht seit Runde 4 in `secondLife/bestand.js`
// (`shopifyProduktUrl`) — dort ist er rein und ohne DOM pruefbar. Spec §5.2
// verlangt das PRODUKT; die Zeile traegt `shopify_product_id`, sobald die
// Backend-Bahn es liefert (Grill W4).

// 🔴 **Fehlercodes und `serverSatz` stehen seit Runde 4 in `secondLife/fehler.js`**
// (Grill W5 / Regel-16-W2). Die Abbildung liegt dort in EINER Datei und ist
// ohne den Baum pruefbar (Hausform `rma/inbox/fehlerklartext.js`). `serverSatz`
// liest nur noch `error` — ein roher Ausnahmetext (`str(exc)`) und ein roher
// Unterstrich-Code (`keine_aggregat_zeile_unit`) erreichen die Oberflaeche
// nicht mehr; der Rueckfall ist ein allgemeiner Satz (`RabattBlock.jsx:52-54`).

/** Eine Skelettzeile — `anzahl` ist die Zahl der SPALTEN, die die Tabelle in
 *  dieser Breite trägt (Bild + sichtbare Datenspalten + ⋮).
 *
 *  🔴 **Sie folgt der Sichtbarkeit, statt eine Acht zu behaupten** (G3b). Mit
 *  einem festen `8` hätte der Ladezustand acht Zellen in eine Tabelle geschrieben,
 *  die bei 1280 nur sechs Spalten trägt — und die zwei zusätzlichen hätten die
 *  Spaltenbreiten mitgezogen (`tableLayout: 'fixed'` nimmt sie aus der ersten
 *  Zeile). Die Zahl kommt deshalb aus DERSELBEN Quelle wie Kopf und Rumpf. */
function ZeilenSkelett({ anzahl }) {
  return (
    <TableRow>
      {Array.from({ length: anzahl }).map((_, i) => (
        <TableCell key={i}>
          <Skeleton variant="text" width={i === 1 ? '80%' : '60%'} />
        </TableCell>
      ))}
    </TableRow>
  );
}

/**
 * Die drei Textstücke der Artikel-Zelle — in DERSELBEN Reihenfolge und OHNE
 * Trennzeichen, wie React sie rendert (`textContent` fügt keins ein).
 *
 * 🔴 **EINE Quelle für ZWEI Verbraucher** (G3a, Plan §2.7b, Grill B1): die Zelle
 * `ArtikelZelle` UND der zugängliche Name des ⋮ (`Actions for <Zelltext>`). Zwei
 * Fassungen desselben Textes driften — und `messeP19` verlangt den Zelltext
 * **wörtlich** im Namen (`helfer.ts:1259-1266`: `nr = zellen[spalte].textContent`).
 *
 * Die drei Stücke: (1) die Kennung; (2) der Name, sonst der Grund-Text, sonst
 * „no base product"; (3) der Sperrgrund, aber NUR wenn Name UND Grund stehen.
 * `grund`/`defekt` reist mit, weil die Zelle die Farbe daraus zieht.
 */
/**
 * Ein Fehler, den `serverSatz` einem CODE zuordnen kann.
 *
 * 🔴 **R16-W5 (2026-11-03):** die Seite meldet Fehler nicht mehr selbst — sie
 * reicht sie an das Detail weiter. Ein Satz, der nur als Text existiert, wuerde
 * dort zum allgemeinen Satz; mit `response.data.error` trifft `fehler.js`
 * seinen Eintrag (`zeile_fehlt`).
 */
function fehlerMitCode(code) {
  const e = new Error(code);
  e.response = { data: { error: code } };
  return e;
}

export function artikelTeile(zeile) {
  const defekt = zeile.grade === DEF;
  const ausgesondert = zeile.state === 'ausgesondert';
  // Der Grund steht als SICHTBARER Text, nie in einem `title` (Regel-16-Blocker
  // aus Teil 1: ein gesperrter MUI-Knopf hat `pointer-events: none` und
  // `tabIndex: -1`, ein Tooltip kaeme nie an).
  const grund = defekt || !zeile.anlegbar
    ? zeile.grund
    : (ausgesondert ? AUSSCHUSS_GRUND : null);
  return {
    sku: zeile.sku,
    zweites: zeile.name ? zeile.name : (grund || 'no base product'),
    drittes: grund && zeile.name ? grund : null,
    grund,
    defekt,
  };
}

/**
 * Der vollständige Zelltext der Artikel-Zelle (Kennung · Name/Grund · Grund) —
 * die Vorlage des ⋮-Namens (`Actions for <zeilenText>`). Die vier
 * Fixture-Zeilen (`second-life-bestand.spec.ts:110-131`) ergeben damit:
 * `010113010102@BAlpha` · `010113010102@DEFAlphaDEF is not sold — the route is
 * the vendor credit` · `020413000102@CBeta` · `030113010102@BGamma`.
 */
export function zeilenText(zeile) {
  const t = artikelTeile(zeile);
  return [t.sku, t.zweites, t.drittes].filter(Boolean).join('');
}

// Die Zelle „Artikel": Nummer, Name — und, wenn die Zeile gesperrt ist, der
// Grund als sichtbarer Text. Der Grund kommt AUS DER ZEILE (`zeile.grund`) und
// steht genau EINMAL: fehlt der Name, traegt ihn die Namenszeile selbst.
function ArtikelZelle({ zeile }) {
  const { sku, zweites, drittes, defekt } = artikelTeile(zeile);
  return (
    <Stack spacing={SP.chip}>
      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: FS.md }}>
        {sku}
      </Typography>
      {zeile.name ? (
        <Typography variant="body2" color="text.secondary" data-testid="artikel-name">
          {zweites}
        </Typography>
      ) : (
        // Kein A-Produkt in shp_products: ein EIGENER Zustand, keine leere
        // Zelle — und der Grund steht hier, nicht ein zweites Mal darunter.
        <Typography variant="body2" color="text.disabled" data-testid="artikel-name">
          {zweites}
        </Typography>
      )}
      {drittes && (
        <Typography variant="caption" color={defekt ? 'error.main' : 'text.secondary'}>
          {drittes}
        </Typography>
      )}
    </Stack>
  );
}

function BestandZeile({
  zeile, onGespeichert, onRabatt, beschreibungAktiv, mobil, sichtbar, spaltenBreite, editorAuftrag,
}) {
  const [offen, setOffen] = useState(false);
  const [text, setText] = useState(zeile.desc_text ?? '');
  // 🔴 **R15-W1 / R16-B4:** der Text-Schritt im Detail fuehrt in die TABELLE.
  // Bis hierher sprang `onZumEditor` nur an den Tabellenanfang — bei 50 Zeilen
  // suchte der Nutzer die Zeile selbst, und der Editor war nicht offen. Jetzt
  // oeffnet die ZEILE ihren Editor und rollt sich in den Blick.
  const zeileRef = useRef(null);
  // 🔴 **Kein `stand` mehr** (Grill B1 / R15 B1 der Runde 2). Der Editor schickte
  // den Stand mit, den er beim Oeffnen zeigte; die Route hielt ihn gegen den
  // LIVE-Text. Hier steht aber der **Zustandsabsatz**, im Shop der **Wrapper**
  // (Absatz + A-Beschreibung) — fuer jede von POG angelegte Zeile war das
  // strukturell verschieden, und die Wache meldete bei jedem ersten Speichern
  // eine Fremdaenderung, die es nicht gab. Die Route leitet den Vergleichswert
  // jetzt selbst her; ein mitgeschickter `stand` hat keine Wirkung mehr.
  //
  // Die Vorschau kommt vom SERVER: „so wie sie im Shop erscheint" heisst
  // Zustandsabsatz **plus** A-Beschreibung — und die A-Beschreibung kennt die
  // Seite nicht. `null` = zu; `''` ist eine gueltige (leere) Vorschau.
  const [vorschauHtml, setVorschauHtml] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState(null);
  const [konflikt, setKonflikt] = useState(null);

  useEffect(() => {
    if (!editorAuftrag || editorAuftrag.sku !== zeile.sku) return;
    setText(zeile.desc_text ?? '');
    setVorschauHtml(null);
    setOffen(true);
    // Nach dem Rendern rollen — die Editor-Zeile entsteht erst mit `offen`.
    const raf = window.requestAnimationFrame(() => {
      zeileRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    });
    return () => window.cancelAnimationFrame?.(raf);
    // `editorAuftrag.n` zaehlt die Auftraege: ein zweiter Klick oeffnet erneut.
  }, [editorAuftrag, zeile.sku, zeile.desc_text]);

  // 🔴 **Die `id` des sichtbaren Sperrgrundes** (Plan §3.1, 2026-10-14). Der
  // gesperrte „Edit"-Knopf zeigt per `aria-describedby` darauf — dieselbe
  // Hausform wie in der offenen Liste (`secondLife/OpenCases.jsx`): der Satz
  // steht EINMAL im DOM, statt als zweite, `visuallyHidden`-Kopie daneben.
  const sperrGrundId = useId();

  const defekt = zeile.grade === DEF;
  // 🔴 Ausschuss wird nicht verkauft — eine Kundenbeschreibung dafuer zu pflegen
  // ist sinnlos. Dieselbe Sperre wie fuer DEF, mit demselben sichtbaren Grund.
  const ausgesondert = zeile.state === 'ausgesondert';
  // 🔴 **Die Sperre haengt an `hat_produkt`, nicht an der Kladden-Kennung `id`**
  // (Grill-Blocker B2): eine `discovered`-Zeile HAT eine Kladde, aber KEIN
  // Produkt — die Route
  // antwortete 404 `zeile_fehlt`, und der Satz dazu („Dieser Fall hat keine
  // Kladdenzeile") war das Gegenteil der Wahrheit. Die Regel steht in
  // `beschreibungGrund` — eine Stelle, nicht zwei.
  const sperrGrund = beschreibungGrund(zeile);
  // 🔴 **Der Schalter gehoert zur Sperre** (Grill W7, 2026-10-09).
  // `SECOND_LIFE_BESCHREIBUNG_AKTIV` steht auf prod AUS, und
  // `POST /<id>/beschreibung` prueft ihn **vor** allem anderen — auch vor der
  // Vorschau. Der Satz der Absage war richtig, der Knopf trotzdem bedienbar:
  // jeder Speicherversuch lief in `beschreibung_aus`. Die Route meldet den
  // Schalterzustand jetzt mit (`GET /bestand` → `beschreibung_aktiv`), und die
  // Sperre haengt daran. Fail-closed: fehlt das Feld, gilt der Schalter als aus.
  const kannBearbeiten = sperrGrund === null && beschreibungAktiv === true;
  // 🔴 **Der Rabatt ist eine ZWEITE Sache, die diese Zeile bearbeitet** — und
  // fuer eine `discovered`-Zeile die EINZIGE: ohne Shopify-Produkt ist die
  // Beschreibung gesperrt, der Rabatt aber genau der fehlende Schritt vor dem
  // Anlegen (`rabattMoeglich`, Begruendung dort). Deshalb oeffnet der Knopf die
  // Zeile, sobald EINE der beiden Sachen bedienbar ist.
  const rabattDa = rabattMoeglich(zeile);
  const kannOeffnen = kannBearbeiten || rabattDa;
  // 🔴 **Die Einheiten-Zeile gilt auch in der TABELLE** (Regel-16-W3 der Runde 5).
  // `rabattMoeglich` ist fuer `kind !== 'aggregate'` false (`bestand.js:341-343`) —
  // der Knopf also gesperrt. Der Grund kam bis hierher aus `beschreibungGrund`
  // und lautete fuer eine Zeile ohne Shopify-Produkt „No Shopify product —
  // create it first via “Create”" (`OHNE_PRODUKT_GRUND`). 🔴 **„Create" ist
  // seit derselben Runde genau fuer diese Zeile GESPERRT** (`KEIN_AGGREGAT_GRUND`,
  // dieselbe Bedingung wie in der Liste, `OpenCases.jsx:206`): der Satz nannte
  // damit einen Weg, den es auf keiner der zwei Flaechen gibt. Die Tabelle nennt
  // jetzt denselben Grund wie die Karte. Fail-closed wie `rabattMoeglich`: ein
  // FEHLENDES `kind` gilt als Einheiten-Zeile.
  const keinAggregat = zeile.kind !== 'aggregate';
  // 🔴 **Der Zustandsgrund steht VOR dem Einheiten-Grund — aber nur der
  // Zustandsgrund.** Grill W8 der Runde 6: die Zeile lautete
  // `keinAggregat ? KEIN_AGGREGAT_GRUND : sperrGrund` und ueberstimmte damit
  // JEDEN Zustandsgrund. Fuer eine `live`-Zeile stand dann „wird im naechsten
  // Erkennungslauf zusammengefasst — erst dann laesst sie sich anlegen",
  // und nach dem Lauf war sie WEITER gesperrt: sie ist `live`. Der Satz
  // versprach einen Weg, der nicht aufgeht.
  //
  // ⚠️ Die Vorfahrt ist deshalb NICHT einfach umgedreht. `OHNE_PRODUKT_GRUND`
  // nennt den Weg „Create" — und genau der ist fuer eine Einheiten-Zeile
  // gesperrt (der Absatz darueber). Dort gewinnt weiter der Einheiten-Grund;
  // nur `VEROEFFENTLICHT_GRUND` (und jeder spaeter ergaenzte Zustandsgrund)
  // geht ihm vor.
  const sperrGrundText =
    keinAggregat && (!sperrGrund || sperrGrund === OHNE_PRODUKT_GRUND)
      ? KEIN_AGGREGAT_GRUND
      : sperrGrund;
  // Der Grund steht genau EINMAL: `ArtikelZelle` zeigt ihn schon fuer DEF,
  // Ausschuss und fehlendes Grundprodukt. Hier erscheinen nur die zwei Faelle,
  // die dort nicht stehen (kein Shopify-Produkt, Einheiten-Zeile). 🔴 Und nur,
  // wenn der Knopf deshalb WIRKLICH gesperrt ist: ist der Rabatt bedienbar,
  // oeffnet er die Zeile, und der Beschreibungs-Grund steht dann im Editor am
  // Feld.
  const zeigeSperrGrund =
    !kannOeffnen && !defekt && !ausgesondert && zeile.anlegbar
    && (keinAggregat || sperrGrund);
  const herkunft = bestandHerkunft(zeile);
  // 🔴 **Der Editor nennt seine Quelle** (Klickdummy-Nachzug, Punkt 5). Die
  // Route reicht `description_source` roh durch (`_beschreibung`); hier wird der
  // Token-String zum Satz. `null`, wenn es keine gibt — ein von Hand geaenderter
  // Entwurf traegt ausdruecklich keine Herkunft, und dann steht dort nichts
  // (`bestand.js::quelleText`).
  const beschreibungsQuelle = quelleText(zeile.description_source);

  const vorschauUmschalten = async () => {
    // Zuklappen braucht keinen Server.
    if (vorschauHtml != null) { setVorschauHtml(null); return; }
    setBusy(true);
    setFehler(null);
    try {
      // 🔴 DIESELBE Route mit `vorschau: true` — aber als EIGENE Funktion.
      // `beschreibungSchreiben` traegt den Schluessel nicht mehr: ein Optionsfeld
      // fuer zwei Absichten (schreiben / nur ansehen) ist die Fehlerklasse, die
      // Blocker B1 war.
      setVorschauHtml(await beschreibungVorschau(zeile.id, text));
    } catch (err) {
      setFehler(serverSatz(
        err, 'The preview could not be loaded. Please try again later.'));
    } finally {
      setBusy(false);
    }
  };

  const speichern = async (bestaetigt = false) => {
    setBusy(true);
    setFehler(null);
    setKonflikt(null);
    try {
      await beschreibungSchreiben(zeile.id, text, { bestaetigt });
      setOffen(false);
      onGespeichert();
    } catch (err) {
      const d = err?.response?.data;
      if (err?.response?.status === 409 && d?.error === 'fremdaenderung') {
        // Beide Fassungen zeigen — nicht stillschweigend ueberschreiben.
        setKonflikt({ live: d.live, pog: d.pog });
      } else {
        setFehler(serverSatz(
          err, 'The description could not be saved. Please try again later.'));
      }
    } finally {
      setBusy(false);
    }
  };

  // 🔴 **EINE Quelle für den Editor** (G3b). Er stand bis hierher NUR im
  // Tabellenzweig; mit der Karte gibt es einen ZWEITEN Ort, an dem er erscheinen
  // muss. Zwei Abschriften wären die Sorte Kopie, die ihren Gegenstand
  // überlebt — hier: die Rabatt- und Beschreibungsbedienung driftet zwischen
  // Telefon und Desktop, und die Proben der einen Hälfte sagen nichts über die
  // andere. Er steht deshalb als VARIABLE vor dem `return`, und beide Zweige
  // lesen DIESELBEN Elemente (dieselben Handler, derselbe `text`-Zustand).
  //
  // ⚠️ Der Editor bleibt INNERHALB von `BestandZeile` — die vier Zustände
  // (`offen`, `text`, `busy`, `vorschauHtml`) und die zwei Handler (`speichern`,
  // `vorschauUmschalten`) sind an EINER Stelle, nicht in zwei Renderern. Ein
  // Auslagern in einen eigenen Baustein hätte den Zustand durch zwei Props
  // gereicht, ohne etwas zu teilen, was heute geteilt werden muss.
  const editorInhalt = (
    <Stack spacing={SP.sm} sx={{ py: SP.sm }}>
      {/* 🔴 **Der Rabatt — die Bedingung des Anlegens** (Grill-Blocker
          B1, 2026-10-09). Er steht IM aufgeklappten Editor, nicht in
          jeder Zeile: die leere Editor-Zeile hat die Zeilenzahl schon
          einmal verdoppelt (Regel-16-Befund W4 der Runde 2), und ein
          Rabattfeld in 777 Zeilen waere derselbe Fehler mit Zahlen.
          Das Bauteil ist `RabattBlock` — die Route liefert dafuer den
          Zeilenvertrag (`second_life_bestand.py`). */}
      {rabattDa && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: SP.chip }}>
            Discount
          </Typography>
          <RabattBlock zeile={zeile} busy={busy} onGeaendert={onRabatt} />
        </Box>
      )}
      <TextField
        size="small"
        fullWidth
        multiline
        minRows={3}
        label="Condition paragraph (draft)"
        value={text}
        disabled={!kannBearbeiten}
        onChange={(e) => setText(e.target.value)}
        helperText={kannBearbeiten
          ? 'The paragraph sits in the draft above the original item’s description; it goes into the Shopify draft, not into the published shop.'
          // 🔴 Der Grund steht SICHTBAR am Feld, wenn es gesperrt ist —
          // hier, nicht mehr in der Aktionsspalte: bei einer Zeile ohne
          // Shopify-Produkt ist der Rabatt bedienbar, der Knopf also
          // offen, und der Satz stuende dort ohne Gegenstand.
          : (sperrGrund || 'Editing the description is not enabled.')}
      />
      {/* 🔴 **Die Quelle des Entwurfs steht NEBEN dem Vorschau-Knopf**
          (Klickdummy-Nachzug, Punkt 5) — dieselbe Zeile wie im Dummy
          (`.drawer .row`). Sie steht nur da, wenn es eine gibt: ein von
          Hand geaenderter Entwurf traegt `description_source = NULL`,
          und ein leeres „Source:" waere ein zweiter Zustand fuer
          „gibt es nicht". */}
      <Stack direction="row" spacing={SP.sm} alignItems="center" useFlexGap flexWrap="wrap">
        <Button
          size="small"
          variant="text"
          disabled={busy || !kannBearbeiten}
          onClick={vorschauUmschalten}
        >
          {vorschauHtml != null ? 'Close preview' : 'Preview'}
        </Button>
        {beschreibungsQuelle && (
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid="beschreibung-quelle"
          >
            {beschreibungsQuelle}
          </Typography>
        )}
      </Stack>
      {vorschauHtml != null && (
        // 🔴 „So wie sie im Shop erscheint" — die **komponierte** Fassung
        // vom Server (Zustandsabsatz + A-Beschreibung), nicht der Absatz.
        // Bis zum 2026-10-09 zeigte diese Stelle `text`, also nur den
        // Absatz, obwohl Spec §8 Frage 3 die ganze Beschreibung verlangt
        // (R15 W2). Gerendert ueber den EINEN Sanitizer des Repos.
        <Box
          data-testid="beschreibung-vorschau"
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: PANEL_RADIUS,
            p: SP.sm,
            maxHeight: 240,
            overflow: 'auto',
          }}
          dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(vorschauHtml) || '' }}
        />
      )}
      {fehler && <Alert severity="warning">{fehler}</Alert>}
      {konflikt && (
        <Alert severity="warning">
          <Typography variant="body2" sx={{ mb: SP.chip }}>
            The description was changed by hand in Shopify. Check both
            versions before you save.
          </Typography>
          <Stack spacing={SP.chip} sx={{ mb: SP.sm }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Shopify currently has:</Typography>
              <Box
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: PANEL_RADIUS, p: SP.sm, maxHeight: 160, overflow: 'auto' }}
                dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(konflikt.live) || '' }}
              />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">POG last wrote:</Typography>
              <Box
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: PANEL_RADIUS, p: SP.sm, maxHeight: 160, overflow: 'auto' }}
                dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(konflikt.pog) || '' }}
              />
            </Box>
          </Stack>
          <Button
            size="small"
            variant="contained"
            color="warning"
            disabled={busy}
            onClick={() => speichern(true)}
          >
            Save anyway
          </Button>
        </Alert>
      )}
      <Stack direction="row" spacing={SP.sm} justifyContent="flex-end">
        <Button size="small" onClick={() => { setText(zeile.desc_text ?? ''); setOffen(false); }}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={busy || !kannBearbeiten}
          onClick={() => speichern(false)}
        >
          {busy ? 'Saving…' : 'Save'}
        </Button>
      </Stack>
    </Stack>
  );

  // 🔴 **EINE Quelle für die zwei Zeilenknöpfe** (G3b). Sie standen bis hierher
  // nur in der Tabellenzelle; die Karte (mobil) braucht DIESELBEN — „Edit" und
  // das ⋮ mit Shopify/LSM. Zwei Abschriften hiessen: der Sperrgrund, der
  // Zeilenbezug des ⋮ (`Actions for <Zelltext>`) und die zwei Menüeinträge
  // driften zwischen Telefon und Desktop, und keine Probe der einen Hälfte sieht
  // die andere. Sie stehen deshalb als Fragment VOR dem `return`.
  //
  // ⚠️ **Die zwei Hüllen sind verschieden, der Inhalt nicht:** die Zeile ordnet
  // rechtsbündig in einer 140-px-Spalte (`BEDIENBREITE.aktion`), die Karte
  // mittig in ihren `actions`-Platz. Deshalb wandert nur der INHALT hierher —
  // die ausrichtende `Stack`-Zeile bleibt an ihrem Ort.
  const zeilenKnoepfe = (
    <>
      <Button
        size="small"
        // 🔴 **„Edit" ist der EINE sichtbare Knopf der Zeile** (§9.4: „ein
        // sichtbarer Knopf + ⋮"). `variant="outlined"` — die Hausregel
        // der reservierten letzten Spalte (§1.2/§2.7).
        variant="outlined"
        disabled={!kannOeffnen}
        // 🔴 **Nur wo der Satz wirklich steht** (Plan §3.1, Grill W5).
        // `disabled={!kannOeffnen}` sperrt in VIER Lagen (DEF, Ausschuss,
        // kein Grundprodukt, kein Rabattweg) — der Satz darunter wird
        // aber nur in ZWEI davon gerendert (`zeigeSperrGrund`). Ein
        // UNBEDINGTES `aria-describedby` zeigte auf eine `id`, die es
        // dann nicht gibt: eine Zusicherung ueber Abwesenheit, die
        // nichts mehr prueft.
        aria-describedby={zeigeSperrGrund ? sperrGrundId : undefined}
        // 🔴 **Auf der Karte trägt der sichtbare Knopf den Zeilenbezug**
        // (Regel-16-Warnung W5, Nachbesserung 1). In der Tabellenzeile deckt die
        // Zeilensemantik den Bezug; eine Kartenliste hat keine Tabellenrolle, und
        // ein Screenreader hörte sonst fünfzigmal „Edit". Der sichtbare Text
        // bleibt unverändert — der Name ist `Edit <Zelltext>` bzw. `Close
        // <Zelltext>` und folgt damit demselben `offen`-Zustand wie die
        // Beschriftung. Desktop bleibt bei „Edit" (dort trägt die Zeile den
        // Bezug).
        aria-label={mobil ? `${offen ? 'Close' : 'Edit'} ${zeilenText(zeile)}` : undefined}
        onClick={() => {
          // Beim Oeffnen aus der Zeile neu befuellen — sonst zeigt der
          // Editor den Stand von vor dem letzten Aktualisieren.
          setText(zeile.desc_text ?? '');
          setVorschauHtml(null);
          setOffen((o) => !o);
        }}
      >
        {/* 🔴 **„Edit", nicht „Description"** (Grill-Blocker B1,
            2026-10-09). Die Zeile oeffnet ZWEI Dinge: den Rabatt und den
            Zustandsabsatz. Fuer eine `discovered`-Zeile ohne
            Shopify-Produkt ist die Beschreibung gesperrt, der Rabatt aber
            genau der fehlende Schritt vor dem Anlegen — hiesse der Knopf
            weiter „Description", waere er entweder gesperrt (und der
            Rabatt unerreichbar, 12 von 13 Prod-Zeilen) oder er oeffnete
            unter falschem Namen. */}
        {offen ? 'Close' : 'Edit'}
      </Button>
      {/* 🔴 **Die externen Sprünge wandern ins ⋮** (§9.4 wörtlich:
          „Externe Sprünge (Shopify, LSM) ins ⋮, nicht als Textknöpfe in
          der Zeile"). Vorher standen hier DREI Textknöpfe — P19 war
          damit **doppelt** rot (3 Knöpfe **und** kein ⋮).
          🔴 **Der Name trägt den Zelltext WÖRTLICH** (§2.7b, Grill B1):
          `Actions for <zeilenText>` — dieselbe Quelle wie die Zelle
          (`artikelTeile`), nicht `zeile.sku`.
          ⚠️ **In der TABELLENZEILE ist die N2-Falle gegenstandslos** (die
          Zeile ist kein Knopf); `onPointerDown`-`stopPropagation` braucht
          erst der Kartenzweig (G3b, §2.7b). */}
      {/* 🔴 **Die N2-Hülle am ⋮-Träger** (`pattern-library.md:478`, G0.11/G0.12,
          Plan §2.7b:421-425): `RowActionsMenu` bringt `stopPropagation` nur für
          `onClick` mit, nicht für `onPointerDown` — der Aufrufer hüllt ihn.
          Hausform `ListenMuster.jsx:1085`.
          ⚠️ **Am Kode gemessen (2026-10-18): die Falle ist auf DIESER Karte
          gegenstandslos.** `MobileRecordCard.jsx` trägt **keinen** Ereignis-
          handler — keine Geste, kein `onClick`, kein `onPointerDown` (Volltext-
          grep über die 377 Zeilen: null Treffer). Es gibt hier also keine
          Kartengeste, die den nächsten Tipp verschlucken könnte; die Hülle ist
          **vorsorglich** und folgenlos, bis der Baustein je eine Geste bekommt.
          Sie steht hier und nicht im Kartenzweig, weil sonst zwei Fassungen der
          Knopfzeile entstünden. 🔴 Der Plan setzt eine Geste voraus, die der
          Baustein nicht hat — Abweichung im Bericht. */}
      <Box onPointerDown={(e) => e.stopPropagation()}>
        <RowActionsMenu
          label={`Actions for ${zeilenText(zeile)}`}
          actions={[
            {
              key: 'shopify',
              label: 'Shopify',
              onClick: () => window.open(shopifyProduktUrl(zeile), '_blank', 'noopener,noreferrer'),
            },
            {
              key: 'lsm',
              label: 'LSM',
              onClick: () => window.open(
                lsmArticleSearchUrl(zeile.basis || zeile.sku), '_blank', 'noopener,noreferrer'),
            },
          ]}
        />
      </Box>
    </>
  );

  // 🔴 **Der Kartenzweig (mobil, §2.6/N11.1).** Unter `md` (900 px) ersetzt die Karte die
  // Tabellenzeile: dieselben Felder, untereinander statt nebeneinander. Bei 390 px
  // ist eine Tabelle eine waagerechte Rollfläche, und die reservierte letzte Spalte
  // (die Zeilenaktion) stünde außerhalb des Bildes — auf `/overview` (G2b) und in
  // der offenen Liste derselben Seite ist der Zweig derselbe.
  //
  // 🔴 **Der Editor bleibt INNERHALB dieser Komponente** (s. `editorInhalt`) — der
  // Kartenzweig liest DIESELBEN Elemente wie der Tabellenzweig, mit demselben
  // `offen`-Zustand. „Edit" wirkt auf der Karte also genau wie in der Zeile.
  if (mobil) {
    const teile = artikelTeile(zeile);
    // 🔴 **Der Sperrgrund steht GENAU EINMAL** (§2.7, Grill W5). `teile.drittes`
    // trägt ihn für DEF, Ausschuss und fehlendes Grundprodukt (er stand in der
    // Zeile in der Artikel-Zelle), `sperrGrundText` für die zwei Fälle, die dort
    // nicht stehen — und `zeigeSperrGrund` schliesst die erste Gruppe ausdrücklich
    // aus (`!defekt && !ausgesondert && zeile.anlegbar`). Die zwei Zweige sind
    // damit disjunkt, und keiner der zwei Sätze steht zweimal auf der Karte.
    const grundText = teile.drittes || (zeigeSperrGrund ? sperrGrundText : null);
    return (
      <Box>
        <MobileRecordCard
          // Die Marke am Datensatz, nicht an der Rolle (Hausform
          // `offene-zeile-<i>` daneben, `po-karte-<id>` in G2b).
          data-testid={`bestand-karte-${zeile.sku}`}
          // 🔴 Das Bild ist auf der Karte der `leading`-Platz — dieselbe
          // Vorschau, dieselbe Grösse wie die Bildspalte der Tabelle. Auf der
          // Karte trägt es zusätzlich die Wiedererkennung, die in der Zeile die
          // eigene Spalte leistet (§2.6 „Image | kern | trägt die
          // Wiedererkennung").
          leading={<ProductThumb src={zeile.bild} alt="" size={40} />}
          // 🔴 **Der Titel ist der NAME, die Kennung steht darunter**
          // (`MobileRecordCard` §@props: `id` = „der Slot der Kennung, direkt
          // unter der Titelzeile"). Das ist die Hausform der offenen Liste auf
          // DERSELBEN Seite (`OpenCases.jsx`: `title={z.name || 'no base
          // product'}`, `id={z.sku}`) — die Kennung ist die SKU, nicht der Name.
          // ⚠️ Auf `/overview` (G2b) ist es umgekehrt, weil dort die PO-Nummer
          // der EINZIGE Name des Datensatzes ist; hier hat die Zeile beide, und
          // die SKU ist die maschinenlesbare (monospace, nie gekürzt).
          title={(
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {teile.zweites}
            </Typography>
          )}
          id={zeile.sku}
          // 🔴 **N11.2** (Titel → Kennung → Status → Kerndaten): die Vorgabe des
          // Bausteins ist `'meta-first'`. Die globale Drehung ist VERWORFEN und
          // gemessen (`ui2-listen-karten.spec.ts` L.1g — die Frist der
          // Fälligkeitskarte rutschte um ≈ 28 px nach unten); das Opt-in an
          // dieser Karte ist der Weg.
          slotOrder="chips-first"
          // P15 liest die zwei Plätze, deren REIHENFOLGE seine Aussage ist
          // (N11.2), und P20/P22 zählen über `karte-kennung-`/`karte-status-`.
          // 🔴 `title` bekommt KEINE Marke: die Kennung dieser Seite sitzt im
          // `id`-Platz, und keine Probe liest den Titel.
          fieldTestIds={{
            id: `karte-kennung-${zeile.sku}`,
            chips: `karte-chips-${zeile.sku}`,
            meta: `karte-meta-${zeile.sku}`,
          }}
          chips={(
            // 🔴 Dieselben `Pill`s wie die Tabelle (N11.3: „Chips in derselben
            // Variante") — die Stufe und der Shopify-Zustand, in derselben
            // Reihenfolge wie die zwei Spalten.
            <>
              <Pill tone={STUFE_TON[zeile.grade] || 'plain'}>{zeile.grade}</Pill>
              <Pill
                tone={ZUSTAND_TON[zeile.state] || 'plain'}
                nowrap
                // 🔴 Der STATUS der Zeile — dieselbe Aussage wie die
                // Shopify-Spalte (`statusSpalte: 5`, §5.3). P22 zählt mobil
                // über diese Marke und verlangt Text oder ein benanntes Symbol;
                // das `Pill` trägt den Zustandsnamen als Text, also nie „nur
                // Farbe" (§9.2 N9.8).
                data-testid={`karte-status-${zeile.sku}`}
              >
                {ZUSTAND_LABEL[zeile.state] || zeile.state}
              </Pill>
            </>
          )}
          meta={(
            // Die Kerndaten der Zeile: Stock, Price, Description — und der
            // Sperrgrund, wo die Zeile ihn zeigt.
            <Stack spacing={SP.chip}>
              {grundText && (
                <Typography
                  variant="caption"
                  // 🔴 **Das Ziel der `aria-describedby` des Edit-Knopfs**
                  // (Regel-16-Blocker B1, Nachbesserung 1). Der Knopf trägt die
                  // Referenz in BEIDEN Zweigen (`zeilenKnoepfe`), das Ziel stand
                  // bis hierher nur in der Tabellenzelle — auf dem Telefon zeigte
                  // die Referenz damit ins Leere (WCAG 4.1.2). Die `id` steht
                  // genau dann, wenn der sichtbare Text der Sperrgrund IST; bei
                  // DEF/Ausschuss/fehlendem Grundprodukt zeigt `teile.drittes`
                  // einen anderen Satz, und `zeigeSperrGrund` ist dann false.
                  id={zeigeSperrGrund && grundText === sperrGrundText ? sperrGrundId : undefined}
                  // 🔴 Derselbe Ton wie in der Zeile: der Zustandsgrund der
                  // Artikel-Zelle trägt bei DEF `error.main`, sonst
                  // `text.secondary`; der Sperrgrund der Aktionsspalte trägt den
                  // Warnton als SCHRIFT (`v2Tone(t,'warn').main`, nicht
                  // `palette.warning.main` — das liefert auf hellem Papier
                  // 3,113:1). Zwei Quellen für denselben Satz hätten hier zwei
                  // Farben für denselben Zustand.
                  sx={{ color: teile.defekt ? 'error.main' : (t) => v2Tone(t, 'warn').main }}
                >
                  {grundText}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                {`Stock ${bestandText(zeile.stock)}${herkunft ? ` (${herkunft})` : ''}`
                  + ` · Price ${euroText(zeile.price_eur)}`
                  + (vergleichspreisText(zeile) ? ` (was ${vergleichspreisText(zeile)})` : '')
                  + (rabattText(zeile.rabatt_pct) ? ` · ${rabattText(zeile.rabatt_pct)}` : '')
                  + ` · Description ${BESCHREIBUNG_LABEL[zeile.desc] || 'Fehlt'}`}
              </Typography>
            </Stack>
          )}
          actions={(
            <Stack direction="row" spacing={SP.chip} alignItems="center" useFlexGap>
              {zeilenKnoepfe}
            </Stack>
          )}
        />
        {/* 🔴 Der Editor klappt unter der Karte auf — dieselbe `Collapse`, derselbe
            Inhalt wie in der Editor-Zeile. Er steht in DEMSELBEN Kasten wie die
            Karte, damit die Liste ihn als EINEN Eintrag mit Abstand führt. */}
        {offen && <Collapse in appear>{editorInhalt}</Collapse>}
      </Box>
    );
  }

  return (
    <>
      <TableRow hover ref={zeileRef}>
        {/* 🔴 Die Bildspalte ist FEST und immer da (§2.6, `BILD_SPALTE_PX`).
            Die Zahl steht an der Kopfzelle — `table-layout: fixed` nimmt die
            Breiten aus der ERSTEN Zeile (N9.5). */}
        <TableCell sx={{ width: BILD_SPALTE_PX, px: SP.tight }}>
          <ProductThumb src={zeile.bild} alt="" size={40} />
        </TableCell>
        {sichtbar.has('item') && (
        <TableCell sx={spaltenBreite('item')}>
          <ArtikelZelle zeile={zeile} />
        </TableCell>
        )}
        {sichtbar.has('level') && (
        <TableCell sx={spaltenBreite('level')}>
          <Pill tone={STUFE_TON[zeile.grade] || 'plain'}>{zeile.grade}</Pill>
        </TableCell>
        )}
        {sichtbar.has('stock') && (
        <TableCell align="right" sx={spaltenBreite('stock')}>
          <Stack spacing={SP.chip} alignItems="flex-end">
            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {bestandText(zeile.stock)}
            </Typography>
            {herkunft && (
              // 🔴 Eine LSM-Menge ist eine Lagermenge, keine verkaufbare Menge
              // am Shopify-Standort. Sie wird sichtbar gekennzeichnet.
              <Typography variant="caption" color="text.secondary">({herkunft})</Typography>
            )}
          </Stack>
        </TableCell>
        )}
        {sichtbar.has('shopify') && (
        <TableCell sx={spaltenBreite('shopify')}>
          {/* 🔴 **Kein Umbruch im Zustands-Etikett** (Owner-Foto-Befund C der
              Nachbesserung 2, `docs/superpowers/plans/2026-10-13-second-life-offene-faelle.md`
              §16): „No product" und „Not listed" sind zwei Wörter, und in der
              `Shopify`-Spalte brach das Etikett ohne `nowrap` um („No /
              product"). Dieselbe Hausform wie in der offenen Liste
              (`secondLife/OpenCases.jsx`) — `nowrap` ist der Schalter des
              Bausteins, nicht seine Vorgabe. */}
          <Pill tone={ZUSTAND_TON[zeile.state] || 'plain'} nowrap>
            {ZUSTAND_LABEL[zeile.state] || zeile.state}
          </Pill>
        </TableCell>
        )}
        {sichtbar.has('price') && (
        <TableCell align="right" sx={spaltenBreite('price')}>
          {/* 🔴 **Drei Zahlen, nicht eine** (Klickdummy-Nachzug, Punkt 2): der
              Preis, der DURCHGESTRICHENE Vergleichspreis und der Rabatt. Erst
              das Durchgestrichene macht den Rabatt auf einen Blick lesbar — ohne
              ihn steht „85,90 €" da und niemand sieht, wovon.
              ⚠️ Fehlt `vergleichspreis_eur`, steht dort NICHTS — kein
              Geviertstrich und keine 0 (`vergleichspreisText`). Ein Artikel, der
              nie teurer war, hat keinen alten Preis; „unbekannt" waere eine
              zweite Aussage fuer dieselbe Sache. */}
          <Stack spacing={SP.chip} alignItems="flex-end">
            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {euroText(zeile.price_eur)}
            </Typography>
            {vergleichspreisText(zeile) && (
              <Typography
                variant="caption"
                color="text.secondary"
                data-testid="vergleichspreis"
                sx={{ textDecoration: 'line-through', fontVariantNumeric: 'tabular-nums' }}
              >
                {vergleichspreisText(zeile)}
              </Typography>
            )}
            {rabattText(zeile.rabatt_pct) && (
              <Typography variant="caption" color="text.secondary">
                {rabattText(zeile.rabatt_pct)}
              </Typography>
            )}
          </Stack>
        </TableCell>
        )}
        {sichtbar.has('beschreibung') && (
        <TableCell sx={spaltenBreite('beschreibung')}>
          {/* 🔴 S1/§4.1: die Zelle zeigt den ENTWURF, nicht nur die Pille — sie
              trägt den Überschuss als elastische Spalte nur, WEIL sie ihn zeigt.
              Der Clamp ist NUR CSS (`WebkitLineClamp`): der volle Text bleibt im
              DOM, nichts wird per `aria-hidden` abgeschnitten (UX-Punkt 3).
              Ohne Entwurf steht neben der Pille eine gedämpfte Zeile mit dem
              Grund aus der Schritt-Logik (UX-Punkt 1) — kein leerer Kasten. */}
          <Stack spacing={SP.chip} alignItems="flex-start">
            <Pill tone={BESCHR_TON[zeile.desc] || 'plain'}>
              {BESCHREIBUNG_LABEL[zeile.desc] || 'Fehlt'}
            </Pill>
            {zeile.desc_text ? (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: '-webkit-box',
                  WebkitLineClamp: BESCHREIBUNG_VORSCHAU_ZEILEN,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {zeile.desc_text}
              </Typography>
            ) : (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {textKurz(zeile) || 'No draft yet'}
              </Typography>
            )}
          </Stack>
        </TableCell>
        )}
        {/* 🔴 Die ⋮-Spalte trägt `BEDIENBREITE.aktion` (140, §2.6) und ist IMMER
            da. Ohne die erklärte Breite bekäme sie mit `table-layout: fixed`
            0 px — die Zeilenaktion läge dann über der letzten Datenspalte (F1). */}
        <TableCell align="right" sx={{ width: BEDIENBREITE.aktion }}>
          <Stack spacing={SP.chip} alignItems="flex-end">
            <Stack direction="row" spacing={SP.chip} justifyContent="flex-end" alignItems="center" useFlexGap>
            {zeilenKnoepfe}
            </Stack>
            {zeigeSperrGrund && (
              // 🔴 Sichtbarer Text, kein `title`: ein gesperrter MUI-Knopf hat
              // `pointer-events: none` und `tabIndex: -1`, ein Tooltip kaeme nie an
              // (Regel-16-Befund aus Teil 1).
              // 🔴 **Und er traegt seit dem 2026-10-14 die `id`, auf die der
              // Knopf zeigt** (Plan §3.1) — der Grund steht EINMAL im DOM.
              // 🔴 **Derselbe Ton wie in der offenen Liste** (Regel-16-W3,
              // 2026-10-14): „warum ist dieser Knopf gesperrt" sah auf zwei
              // Flaechen derselben Seite verschieden aus (`warning.main` in der
              // Liste, `text.secondary` hier). EINE Hausform fuer den Sperrgrund
              // — `v2Tone(t,'warn').main`, der Warnton als SCHRIFT.
              <Typography
                id={sperrGrundId}
                variant="caption"
                sx={{ color: (t) => v2Tone(t, 'warn').main, textAlign: 'right' }}
              >
                {sperrGrundText}
              </Typography>
            )}
          </Stack>
        </TableCell>
      </TableRow>
      {/* 🔴 **Die Editor-Zeile wird NUR bei `offen` gerendert** (Regel-16-Befund W4
          der Runde 2). Vorher stand sie immer da, und `unmountOnExit` sparte nichts —
          es raeumt den INHALT der Zelle weg, nicht die Tabellenzeile. Bei 777 Zeilen
          verdoppelte sich damit die Zeilenzahl der Tabelle. */}
      {offen && (
      <TableRow>
        {/* 🔴 Die Zahl folgt der SICHTBARKEIT: die Editor-Zeile deckt die
            Bildspalte, die sichtbaren Datenspalten UND die ⋮-Spalte. Ein festes
            8 deckte auf schmalem Behälter zu viel (die Tabelle liefe über den
            Rand) und auf keinem Fall zu wenig. */}
        <TableCell colSpan={1 + sichtbar.size + 1} sx={{ py: 0 }}>
          <Collapse in appear>
            {editorInhalt}
          </Collapse>
        </TableCell>
      </TableRow>
      )}
    </>
  );
}

/**
 * Der **Frische-Stempel** des Kopfes — „● Stock as of 04:30 UTC"
 * (Klickdummy-Nachzug, Punkt 1).
 *
 * 🔴 **Warum er ueberhaupt dasteht.** Die Route holt den Bestand bei jedem
 * Aufruf neu (LSM + Shopify, kein Nachtlauf) und stempelt die Antwort mit
 * `geholt_am`. Die Seite zeigte das nirgends: eine Tabelle, die 777 Zeilen
 * behauptet, ohne ein Datum zu nennen, ist eine Zahl ohne Haltbarkeit — der
 * Leser kann nicht entscheiden, ob er einen Stand von jetzt oder von gestern
 * vor sich hat.
 *
 * 🔴 **Die Zeit ist UTC** (`bestand.js::stempelText`) — der Stempel der Route
 * ist eine UTC-Zeit, und eine Anzeige in der Zone des Betrachters waere je
 * Rechner eine andere Aussage.
 *
 * ⚠️ Der Punkt pulsiert wie im Dummy, aber `prefers-reduced-motion` schaltet die
 * BEWEGUNG ab (dieselbe Hausform wie `OpenCases.jsx`); der Stempel bleibt.
 * ⚠️ Ohne `geholt_am` rendert die Komponente NICHTS — kein „00:00 UTC".
 *
 * 🔴 **Und bei ausgefallener Bestandsquelle nennt der Stempel sie** (Regel-16-W1
 * der Runde 1, 2026-10-14). „Stock as of 13:47 UTC" ist der Satz, der bei
 * gesetztem `lsm_fehler` (oder `bestand_fehler`) nicht mehr traegt: der Abruf ist
 * frisch, der BESTAND aber nur teilweise bekannt. Der Zusatz kommt aus
 * `bestand.js::stempelZusatz` (dort auch die Begruendung).
 * 🔴 **Der Punkt ist dann kein gruner „alles frisch"-Punkt mehr, sondern
 * `warning.main` und steht STILL.** Ein pulsierender gruener Punkt neben
 * „LSM missing" waere derselbe Widerspruch eine Ebene hoeher — dieselbe Bauform
 * wie der Punkt in `OpenCases.jsx` (`bgcolor` aus dem Zustand, nicht fest).
 */
function FrischeStempel({ geholtAm, lsmFehler, bestandFehler }) {
  const text = stempelText(geholtAm);
  if (!text) return null;
  const zusatz = stempelZusatz({ lsmFehler, bestandFehler });
  return (
    <Stack direction="row" spacing={SP.chip} alignItems="center" sx={{ whiteSpace: 'nowrap' }}>
      <Box
        component="span"
        data-testid="frische-punkt"
        data-quelle-fehlt={zusatz ? 'ja' : 'nein'}
        sx={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          bgcolor: zusatz ? 'warning.main' : 'success.main',
          flex: 'none',
          '@keyframes frischePuls': {
            '0%, 100%': { opacity: 1, transform: 'scale(1)' },
            '50%': { opacity: 0.45, transform: 'scale(0.82)' },
          },
          // Ohne Zusatz pulsiert er; mit Zusatz steht er still (kein „alles
          // frisch", also auch keine Bewegung).
          animation: zusatz ? 'none' : 'frischePuls 2.4s ease-in-out infinite',
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
        {zusatz ? `${text} — ${zusatz}` : text}
      </Typography>
    </Stack>
  );
}

export default function SecondLifeBestand() {
  const [daten, setDaten] = useState(null);
  const [fehler, setFehler] = useState(null);
  // 🔴 **Die Sperre des ERSTEN Aufbaus hängt an einem Merkmal, das im
  // ERFOLGSZWEIG gesetzt wird** (N8.5/P9Sperre, Lehre aus G2a: dort stand es in
  // `.finally`, und die Sperre war tot). `jeGeladen` wird im `.then` von `laden()`
  // gesetzt, NIE in `.finally`. Sperre = `fehler != null && !jeGeladen`.
  const [jeGeladen, setJeGeladen] = useState(false);
  // 🔴 **EIN Hinweis fuer die ganze Seite — und seit der Nachbesserung 3 nur
  // noch EINE Meldung** (R16-C1/R15-N7).
  //
  // Hier stand bis dahin ein Kasten mit den letzten ZWEI Meldungen, einer
  // Klemme und einem Zaehler fuer die herausgefallenen (B4-W1/C1, 2026-10-16).
  // Der war fuer PARALLELE ABSAGEN gebaut: drei Zeilen scheiterten gleichzeitig,
  // zwei passten hinein, die dritte wurde gezaehlt.
  //
  // 🔴 **Dieser Erzeuger ist mit R16-C1 entfallen.** Fehler aus Anlegen,
  // Freigeben, Kladde und Verwerfen stehen jetzt im DETAIL (nahe am Ort, das
  // Detail oeffnet sie ohnehin); der Seitenkasten traegt nur noch die
  // BESTAETIGUNG eines Rabattwechsels — und die kommt einzeln. Ein Zwei-Slot mit
  // Klemme und Zaehler haette fuer sie keinen Erzeuger mehr: er waere gruen,
  // weil nichts mehr da ist. Der Mechanismus ist mit seinen sechs Proben
  // geloescht (Regel 15 §Rueckbau), nicht stehen gelassen.
  //
  // 🔴 **Warum `schluessel` bleibt:** dieselbe Kennung wie bei `laufend` — eine
  // kladdenfreie Zeile hat keine Kennung und **bekommt** eine
  // (`zeilenSchluessel`, `bestand.js`). Die Bestaetigung nennt damit ihre Zeile.
  const [hinweis, setHinweis] = useState(null);

  /**
   * Einen Hinweis zu EINER Zeile setzen — die EINE Stelle dafuer.
   *
   * ⚠️ **Ein neuer Hinweis verdraengt den alten**, auch den einer anderen Zeile:
   * es gibt genau eine Flaeche. Frueher blieb die fremde Meldung stehen
   * (`ohneZeile`, in der Nachbesserung 3 mit seiner Probe geloescht); mit einem
   * einzigen Erzeuger ist das die ehrlichere Form.
   */
  const zeigeHinweis = useCallback((zeile, text) => setHinweis({
    schluessel: zeilenSchluessel(zeile),
    text,
  }), []);

  /** Den Hinweis DIESER Zeile entfernen, ohne einen neuen zu setzen. */
  const raeumeZeile = useCallback(
    (zeile) => setHinweis((alt) => (
      alt && alt.schluessel === zeilenSchluessel(zeile) ? null : alt
    )),
    [],
  );
  const [verwerfen, setVerwerfen] = useState(null);
  /** Der offene Verwerfen-Ausgang — das Detail wartet auf `resolve`/`reject`. */
  const verwerfenWarten = useRef(null);
  // 🔴 **Der Beschaeftigt-Zustand wird JE ZEILE gefuehrt** (Auftrag 2026-10-14).
  // Owner, woertlich: *„Wenn ich bei einem Artikel auf Create druecke, dann
  // rotieren alle Buttons — warum? Ich moechte dass ich das einzeln anklicken
  // kann und dann auch weitere und die sich einreihen von selbst."*
  //
  // Bis dahin stand hier EIN Wert fuer die ganze Liste (`null | 'anlegen' |
  // 'freigeben' | 'verwerfen'`), und `OpenCases` machte daraus
  // `laeuft = Boolean(busy)` + `disabled={laeuft}` — deshalb drehte jede Zeile
  // derselben Art mit und war gesperrt.
  //
  // Die Form ist eine Abbildung `{ [zeilenSchluessel]: art }`. Der Schluessel
  // ist die **SKU** (`bestand.zeilenSchluessel`), nicht die Kladden-Kennung:
  // eine kladdenfreie Zeile hat keine und **bekommt** eine, sobald
  // `stelleKladdeSicher` sie anlegt — mit `id` fiele der Fortschritt genau in
  // dem Moment weg, in dem die Zeile entsteht.
  const [laufend, setLaufend] = useState({});

  /** Eine Zeile als laufend eintragen bzw. austragen — die EINE Stelle dafuer. */
  const setzeLaufend = (schluessel, art) => setLaufend((m) => {
    if (art == null) {
      // ⚠️ `hasOwnProperty`, nicht `in`: die Schluessel sind SKUs aus der
      // Serverantwort, und `'constructor' in {}` waere wahr.
      if (!Object.prototype.hasOwnProperty.call(m, schluessel)) return m;
      const { [schluessel]: _weg, ...rest } = m;
      return rest;
    }
    return { ...m, [schluessel]: art };
  });

  // 🔴 **Ein Schreiber je Seite (§3.3): `useListQuery`.** Die sechs Parameter der
  // Karte (§3.1) liegen in der Adresse, nicht mehr in sechs `useState`. Suche,
  // Filter und Sortierung schreiben `replace`, der Seitenwechsel `push` (N10.2);
  // jede Abfrageänderung setzt die Seite auf 1 (N10.3) — der frühere
  // `useEffect(() => setPage(0), …)` entfällt damit ersatzlos.
  const query = useListQuery({
    dimensions: DIMENSIONEN,
    sort: { default: SORT_VORGABE, values: SORT_VALUES },
    pageSize: ROWS_PER_PAGE,
  });
  const { state, set, clearAll, chips, ignoredValues } = query;

  // 🔴 **Nur die NEUESTE Antwort darf den Zustand setzen** (Auftrag 2026-10-14).
  // Mit dem zeilenweisen Zustand laufen zwei Aktionen PARALLEL und rufen beide
  // `laden()` — vorher gab es hoechstens eine. Ohne diese Nummer koennte die
  // zuerst gestartete, aber spaeter eintreffende Antwort die frischere
  // ueberschreiben, und die Liste zeigte einen Stand, den es nie gab.
  const ladeNr = useRef(0);
  const laden = useCallback(async () => {
    const nr = ++ladeNr.current;
    setFehler(null);
    try {
      const antwort = await bestandLesen();
      // 🔴 **Nur die NEUESTE Antwort setzt den Zustand** (master, 2026-10-14) —
      // und **nur eine angenommene Antwort hebt die Sperre des ersten Aufbaus
      // auf** (G3a, N8.5/P9Sperre). Die zwei Absichten fallen hier zusammen:
      // `jeGeladen` heisst „es liegen Daten vor", nicht „es kam irgendeine
      // Antwort an" — eine verworfene (veraltete) Antwort hat `daten` nicht
      // gesetzt und darf die Zaehler daneben nicht aufwecken.
      if (nr === ladeNr.current) {
        setDaten(antwort);
        // 🔴 **Im ERFOLGSZWEIG** — nur ein gelungener Abruf hebt die Sperre auf.
        setJeGeladen(true);
      }
    } catch (err) {
      // 🔴 **Ein fehlgeschlagenes NACHLADEN wirft die geladenen Zeilen NICHT
      // weg** (Hausform `POInbox.jsx:1063-1069`). `daten` wird hier bewusst
      // NICHT angefasst: es bleibt, was es war — `null` nur beim Erstaufbau,
      // sonst der zuletzt geladene Stand. Ein transienter 500 (LSM nicht
      // erreichbar) nimmt dem Menschen sonst die Liste weg, die er gerade liest.
      if (nr === ladeNr.current) setFehler(serverSatz(err, 'The stock list cannot be loaded.'));
    }
  }, []);

  useEffect(() => { laden(); }, [laden]);

  // „Annehmen"/„Create"/„Release": ein Entwurf wird freigegeben, ein noch
  // nicht angelegter Fall wird angelegt. Beide Wege gehen ueber die BESTEHENDEN
  // Routen. `modus` unterscheidet sie; ohne ihn entscheidet der Zustand
  // (Rueckwaertsvertraeglichkeit mit der alten Karussell-Schnittstelle).
  const annehmen = async (z, modus, opts = {}) => {
    raeumeZeile(z);
    if (z.id == null) {
      // 🔴 **Erreichbar bleibt dieser Zweig nur als Rueckfall.** Seit dem
      // 2026-10-14 sperrt „kein Fall" den Knopf nicht mehr
      // (`OpenCases.zeilenLage`), sondern der fehlende **Rabatt** — und der Weg
      // zur Kladde fuehrt ueber das ⋮ (`stelleKladdeSicher`). Ein Klick kaeme
      // hier also nur an, wenn ein Aufrufer `annehmen` direkt ruft.
      // ⚠️ Die Antwort bleibt trotzdem eine sichtbare Absage: kein stiller
      // Leerlauf, und `createSecondLife(null)` waere eine Anfrage an
      // `/api/second-life/null/anlegen`. Sie geht als Fehler MIT CODE nach
      // oben — das Detail zeigt sie (R16-W5), nicht der Seitenkasten.
      throw fehlerMitCode('zeile_fehlt');
    }
    const art = modus === 'freigeben' || (modus == null && z.state === 'draft')
      ? 'freigeben' : 'anlegen';
    const schluessel = zeilenSchluessel(z);
    setzeLaufend(schluessel, art);
    try {
      if (art === 'freigeben') await approveSecondLife(z.id, { bestaetigt: opts.bestaetigt });
      else await createSecondLife(z.id);
    } catch (err) {
      // 🔴 **Den Rumpf lesen, nicht den Satz** (§4.2, S6): `serverSatz` wirft
      // `alt`/`neu` weg. Der 409 `a_preis_geaendert` wandert deshalb UNGEMAPPT
      // zum Aufrufer (FallDetail), der die Bestätigung mit dem neuen Preis
      // anbietet; erst danach wird der Fehler gemeldet.
      const d = err?.response?.data;
      if (d?.error === 'a_preis_geaendert') {
        // `finally` lädt neu und räumt den Laufend-Zustand; der Fehler wandert
        // danach zum Aufrufer (FallDetail) für die Bestätigung.
        throw err;
      }
      // 🔴 **R16-W5: kein Seitenkasten mehr fuer Wege mit Detailbezug.** Der
      // Fehler wandert zum Aufrufer (`FallDetail`), der ihn EINMAL im Detail
      // zeigt. Der Seitenkasten bleibt fuer Wege ohne Detail (Verwerfen).
      throw err;
    } finally {
      // 🔴 **Immer neu laden — auch im Fehlerfall** (2026-10-14). Mit dem
      // zeilenweisen Zustand laufen zwei Aktionen parallel; die Liste muss
      // danach den Stand der Datenbank zeigen und nicht den, den die Seite
      // vorher hatte. Die Nummer in `laden` sorgt dafuer, dass die NEUESTE
      // Antwort gewinnt.
      await laden();
      setzeLaufend(schluessel, null);
    }
  };

  /**
   * Die **Kladdenzeile** zu einer Zeile ohne RMA-Fall anlegen — der Weg, den es
   * bis zum 2026-10-14 nicht gab (`POST /api/second-life/kladde`).
   *
   * 🔴 **Sie ist die Voraussetzung des Rabattwegs.** `POST /<id>/rabatt` nimmt
   * eine Kennung; eine kladdenfreie Zeile hat keine. `OpenCases` ruft das hier
   * aus dem ⋮-Eintrag „Discount…" heraus und oeffnet den Dialog erst mit der
   * zurueckgegebenen Kennung.
   *
   * Returns:
   *   die neue Kladden-Kennung — oder `null`. **`null` heisst: es wird NICHTS
   *   geoeffnet.** Der Grund steht dann als Hinweis auf der Seite
   *   (`shopify_lesefehler` bei einer gescheiterten Lesung, `kein_grundprodukt`
   *   ohne A-Artikel) — fail-closed, kein stiller Leerlauf.
   */
  const stelleKladdeSicher = async (z) => {
    const schluessel = zeilenSchluessel(z);
    raeumeZeile(z);
    // ⚠️ Die Art heisst `kladde`, nicht `anlegen`: der Knopf legt die KLADDE an,
    // nicht das Produkt — „Creating…" waere dort eine Luege. Die Tabelle fuehrt
    // `FORTSCHRITT_TEXT` (`bestand.js`, seit dem 2026-10-16 dort — sie hat mit
    // der Sammelansage einen zweiten Leser); `kladde` traegt „Preparing…"
    // (Regel-16-Befund B1, 2026-10-14: bis dahin fiel die Art durch und der
    // Vorgang war stumm — kein Spinner, kein Text).
    setzeLaufend(schluessel, 'kladde');
    try {
      const antwort = await kladdeAnlegen(z.sku);
      await laden();
      return antwort?.zeile_id ?? null;
    } catch (err) {
      // 🔴 **R16-W5:** derselbe Weg — der Fehler geht nach oben ins Detail.
      throw err;
    } finally {
      setzeLaufend(schluessel, null);
    }
  };

  /**
   * Verwerfen oeffnen — und dem DETAIL den Ausgang melden.
   *
   * 🔴 **R16-C1 (Nachbesserung 3):** der Fehler eines gescheiterten Verwerfens
   * stand bis hier im Seitenkasten — und der Kasten liegt ueber der LISTE,
   * waehrend der EINZIGE Einstieg zum Verwerfen im DETAIL sitzt. Auf schmalen
   * Viewports war er damit unsichtbar: der Nutzer sah nur, dass nichts
   * passierte. Das Detail oeffnet die Rueckfrage ueber diese Funktion und
   * bekommt ihr Ergebnis als Promise — `resolve` heisst „abgebrochen oder
   * abgelegt", `reject` traegt den Fehler dorthin, wo der Klick herkam.
   */
  const ablehnen = useCallback((zeile) => new Promise((resolve, reject) => {
    verwerfenWarten.current = { resolve, reject };
    setVerwerfen(zeile);
  }), []);

  /** Die Rueckfrage schliessen, ohne zu verwerfen — kein Fehler im Detail. */
  const verwerfenAbbrechen = useCallback(() => {
    setVerwerfen(null);
    const warten = verwerfenWarten.current;
    verwerfenWarten.current = null;
    warten?.resolve?.();
  }, []);

  const verwerfenBestaetigt = async () => {
    const z = verwerfen;
    setVerwerfen(null);
    const warten = verwerfenWarten.current;
    verwerfenWarten.current = null;
    // 🔴 Dieselbe Raeumung wie `annehmen`/`stelleKladdeSicher`: die veraltete
    // Meldung DIESER Zeile faellt heraus, die fremde bleibt. Ohne sie bliebe
    // nach einem gelungenen Verwerfen die alte Absage derselben Zeile stehen.
    raeumeZeile(z);
    if (!z?.id) {
      // 🔴 Kein stiller Leerlauf: der Dialog hat „wird abgelegt" versprochen.
      // Der Satz kommt aus `fehler.js` (`zeile_fehlt`) und steht im DETAIL.
      warten?.reject?.(fehlerMitCode('zeile_fehlt'));
      return;
    }
    // 🔴 **Die Sperre fuehrt auch der VERWERFEN-Weg** (Regel-16-W3 der Runde 6,
    // geschlossen in Runde 7). `annehmen` und `speichern` setzten sie von Anfang
    // an, `verwerfenBestaetigt` nicht — ausgerechnet der EINZIGE unumkehrbare
    // Vorgang der Seite lief ohne. Der Dialog ist hier schon zu (`setVerwerfen`),
    // die Karussell-Knoepfe standen also wieder offen, waehrend das Ablegen lief:
    // ein zweiter Klick auf „Create" haette sich mit ihm ueberholt.
    // 🔴 **Seit dem 2026-10-14 sperrt das nur noch DIESE Zeile** (der
    // Beschaeftigt-Zustand ist zeilenweise) — und die Art `verwerfen` traegt
    // weiterhin KEIN Fortschrittsetikett, weil der Hauptknopf dieser Zeile
    // nichts tut.
    const schluessel = zeilenSchluessel(z);
    setzeLaufend(schluessel, 'verwerfen');
    try {
      await discardSecondLife(z.id);
      warten?.resolve?.();
    } catch (err) {
      // 🔴 **R16-C1:** der Satz geht ins DETAIL, nicht in den Seitenkasten.
      // Der Ersatzsatz ist der des Verwerfens; ein bekannter Code
      // (`zeile_fehlt`) gewinnt ueber ihn (`serverSatz`).
      warten?.reject?.(err);
    } finally {
      await laden();
      setzeLaufend(schluessel, null);
    }
  };

  const zeilen = daten?.zeilen ?? null;

  // 🔴 **Die Bestandsquelle hat nicht geantwortet UND die Liste ist leer**
  // (Regel-16-B1 der Runde 1, 2026-10-14). Das ist die Lage, in der die Flaeche
  // NICHTS behaupten darf: die Zeilen kommen aus LSM (Untertitel „Non-A items
  // **from LSM**"), eine leere Liste ist hier die FOLGE des Ausfalls, kein Beleg
  // fuer „es gibt keine Nicht-A-Artikel". Regel 22 §3 („unbekannt ist nie 0")
  // und §5 (`unknown` = „eine Quelle hat nicht geliefert — die leere Liste
  // bedeutet nichts"). Die drei Zahlen darunter (Zaehler, Pillenzahl,
  // Blatt-Zaehler) sagen in derselben Lage ebenfalls nichts.
  //
  // 🔴 **Nur die KOMBINATION ist der Fall — und das ist gemessen, nicht
  // angenommen:** `lsm_fehler` MIT Zeilen ist der NORMALFALL. Faellt LSM aus,
  // laeuft die Tabelle mit Shopify allein weiter
  // (`second_life_bestand.py:340-344`), und die Route liefert die Zeilen
  // trotzdem — `test_second_life_bestand_route.py:262-265` pinnt genau das
  // („die Zeile ist trotzdem da"). Dort BLEIBEN die Zahlen stehen: sie sind
  // teilweise bekannt (Shopify), und sie zu unterdruecken waere die umgekehrte
  // Verwechslung. Nur die leere Liste ist die Aussage „unbekannt".
  //
  // ⚠️ **Der vierte Fall derselben Klasse:** waehrend des ersten Ladens ist
  // `zeilen` `null` (nicht `[]`), `bestandUnbekannt` also falsch. Pille, Zaehler
  // UND Blatt-Zaehler schuetzen sich dagegen ueber `jeGeladen` (Regel-16 N1) —
  // siehe ihre Aufrufstellen.
  const bestandUnbekannt = Boolean(daten?.lsm_fehler) && zeilen?.length === 0;

  // 🔴 **Die Filter- und Sortierparameter kommen aus der Adresse** (§3.1). Die
  // Facetten liefern immer ein Array (`kind: 'multi'`), leer = keine Einschränkung;
  // der Quick-Filter trägt den Schlüssel `stock`.
  const q = state.q ?? '';
  const stufen = state.level ?? LEER_LISTE;
  const zustandWerte = state.shopify ?? LEER_LISTE;
  const nurBestand = (state.quick ?? LEER_LISTE).includes('stock');
  // `sort` trägt `feld` (aufsteigend) oder `-feld` (absteigend); Vorgabe `-stock`.
  const sortFeld = state.sort ?? SORT_VORGABE;
  const sortKey = sortFeld.startsWith('-') ? sortFeld.slice(1) : sortFeld;
  const sortDir = sortFeld.startsWith('-') ? 'desc' : 'asc';

  const sichtbar = useMemo(
    () => (zeilen
      ? sortiere(filtere(zeilen, { q, stufe: stufen, zustand: zustandWerte, nurBestand }), sortKey, sortDir)
      : []),
    [zeilen, q, stufen, zustandWerte, nurBestand, sortKey, sortDir],
  );

  // 🔴 **Die Anzeige-Seitenweise.** `sichtbar` bleibt die volle gefilterte und
  // sortierte Liste; `seite` ist der Ausschnitt, der im DOM landet. Die Seite
  // wird ABGELEITET geklemmt, nicht per Effekt: filtert der Mensch auf wenige
  // Treffer, waehrend er auf Seite 5 steht, zeigte ein blosses `slice` sonst
  // NICHTS — eine leere Tabelle ohne leeren Filter. Abgeleitet gibt es diesen
  // Zwischenzustand gar nicht. Die zwei Regeln stehen in `bestand.js` und sind
  // dort ohne DOM geprueft (`seiteVon`, `maxSeite`).
  // ⚠️ `useListQuery` führt `page` 1-basiert (fehlt = 1); die Anzeige rechnet
  // 0-basiert und klemmt gegen die Trefferzahl (N10.4).
  const seiteAktiv = Math.min(
    Math.max(0, (Number(state.page) || 1) - 1),
    maxSeite(sichtbar.length, ROWS_PER_PAGE),
  );
  const seite = useMemo(
    () => seiteVon(sichtbar, seiteAktiv, ROWS_PER_PAGE),
    [sichtbar, seiteAktiv],
  );

  // ── Die Breitenmechanik (§2.6) ────────────────────────────────────────────
  // 🔴 **Die Weiche ist die BREITE, nicht die Eingabeart** — dieselbe Regel wie
  // im Baustein selbst (`MobileRecordCard` §@useWhen: „eine zweite Grenze am
  // Zeigertyp formte dieselbe Seite in zwei Breiten verschieden").
  //
  // 🔴 **`md`, nicht `sm`** (Regel-16-Blocker B3, Nachbesserung 1, 2026-10-18).
  // Die Sidebar ist ab `sm` **permanent 240 px** breit, und der Tabellenbehälter
  // ist `Viewport − 360` (240 Sidebar + 40 `main` + 48 `PageContainer` + 32
  // Panel-Innenrand). Mit `sm` rendert die Seite bei 600–899 px eine Tabelle in
  // 240–540 px: die Item-Spalte fiel auf 17–135 px (Minimum 200), SKU und
  // Status-Pille überliefen ihre Zellen. `OpenCases.jsx:391` — die offene Liste
  // DERSELBEN Seite — schaltet aus genau diesem Grund mit `down('md')`; die
  // Bestandsliste zieht nach.
  //
  // 🔴 **Was das für die Proben heisst:** `istSchmal` in `helfer.ts` bleibt bei
  // 600 (es bedient ALLE Listen, die meisten mit `sm`-Weiche). Die Bestandsproben
  // (`second-life.spec.ts`, `second-life-bestand.spec.ts`) lesen die Grenze
  // deshalb selbst bzw. bekommen sie als Argument.
  //
  // 🔴 `noSsr: true` (Hausform `OpenCases.jsx:391`): ohne es rendert der erste
  // Durchgang serverseitig die Vorgabe (`false`) und der Baum springt.
  const theme = useTheme();
  const mobil = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });

  // 🔴 Die MESSUNG sitzt am `liste-tabelle`-Träger, nicht am `TableViewport`:
  // gemessen wird der RAUM (die rohe Innenbreite des Behälters), nicht die
  // gedeckelte Tabelle — sonst mässe sich der Deckel selbst (BW4,
  // `ListenMuster.jsx`/`ProductsTable.jsx` — die auf dieser Basis vorhandene
  // Hausform; G2b ist nicht gemergt).
  const tabelleRef = useRef(null);
  // 🔴 **Der Auftrag „oeffne den Editor dieser Zeile"** (R15-W1 / R16-B4).
  // `n` zaehlt, damit derselbe Klick ein zweites Mal wirkt.
  const [editorAuftrag, setEditorAuftrag] = useState(null);
  const { width: behaelterBreite } = useContainerWidth(tabelleRef);
  // 🔴 Der Rückfall VOR der ersten Messung ist das Muster der Seite
  // (`CONTENT_WIDTH.full` ist `null` → `reading`), genau wie in
  // `TableViewport`/`ListenMuster` — nie 0 (Haus-Satz, §4.3:623-624).
  const messBasis = behaelterBreite ?? CONTENT_WIDTH[MUSTER] ?? CONTENT_WIDTH.reading;
  const { visible: sichtbareSpalten } = useColumnPriority({
    columns: SPALTEN,
    containerWidth: messBasis,
  });
  const sichtbarSpalte = useMemo(
    () => new Set(sichtbareSpalten.map((s) => s.key)),
    [sichtbareSpalten],
  );
  // Das Spaltenbudget = Σ `COLUMN_MAX[kind].max` über die SICHTBAREN Spalten
  // (§4.1:567). `?? 0`: eine Spalte mit unbekannter Art hat kein `max`, und EIN
  // falscher Deskriptor darf nicht die ganze Tabelle reissen.
  const spaltenBudget = sichtbareSpalten.reduce(
    (summe, sp) => summe + (COLUMN_MAX[sp.kind]?.max ?? 0),
    0,
  );
  // Der DECKEL der Tabelle = min(Behälter, Budget) (§4.1:568). `TableViewport`
  // MELDET diesen Wert nur (G0b.4) — das ANWENDEN ist Sache der Ansicht.
  const tabellenMax = Math.min(messBasis, spaltenBudget);

  // 🔴 Die ⋮-Spalte steht NEBEN dem Budget (§4.1:567 zählt nur Datenspalten) —
  // und die BILDSPALTE ebenso (§2.6, s. `BILD_SPALTE_PX`). Mit
  // `tableLayout: 'fixed'` und Datenspalten, die zusammen genau 100 % tragen,
  // bekämen beide 0 px, und die Zeilenaktion läge über der letzten Datenspalte
  // (F1). Die Datenspalten teilen sich deshalb den REST proportional zu ihrem
  // `max`. Dieselbe Bauform wie der G2b-Zweig (`feat/ui2-t10-g2b-mobil-breiten`,
  // `POOverview.jsx`); auf dieser Basis traegt `ProductsTable.jsx` die
  // Untergrenze (`tabellenMin`). Die Auswahlspalte fehlt, weil §13.1 G3 kein
  // BS16 gibt.
  const bedienSumme = BEDIENBREITE.aktion + BILD_SPALTE_PX;

  // 🔴 **Die Rollschwelle** (Regel-15-Blocker B1, Nachbesserung 1, 2026-10-18):
  // die Tabelle darf ihre Kernspalten NIE unter `COLUMN_MAX[kind].min`
  // quetschen. Unterhalb der Schwelle rollt der `TableViewport` waagerecht —
  // und erst DANN haben `stickyFirstColumn` und `edgeHint` einen Gegenstand.
  //
  // Zwei Zahlen gehören dazu, und beide stehen im Kode:
  //   1. `spaltenMinSumme` = Σ `COLUMN_MAX[kind].min` der SICHTBAREN Spalten +
  //      Bild + ⋮ — die Untergrenze aus der Gate-Auflage (vier Kernspalten:
  //      448 + 196 = **644**).
  //   2. `spaltenMinBreite` = die Hausform aus G2b (Zweig
  //      `feat/ui2-t10-g2b-mobil-breiten`, `POOverview.jsx` `spaltenMinBreite`):
  //      die Breite, ab der die PROPORTIONALE Verteilung JEDE Spalte über ihrem
  //      Minimum hält. Aus `Breite_i = max_i/Budget × (T − bedien) ≥ min_i`
  //      folgt `T ≥ bedien + min_i × Budget/max_i`; das Maximum über die
  //      sichtbaren Spalten ist die Schwelle. Sie ist bei diesem Spaltensatz
  //      immer ≥ (1) — vier Kernspalten: 196 + 200 × 920/360 = **707** — und
  //      genau das ist der Punkt: (1) allein reicht NICHT, weil die Verteilung
  //      die Item-Spalte bei T = 644 auf 175 px drückte (unter `min` 200).
  //      Deshalb gilt das Maximum der beiden.
  const spaltenMinSumme = bedienSumme + sichtbareSpalten.reduce(
    (summe, sp) => summe + (COLUMN_MAX[sp.kind]?.min ?? 0),
    0,
  );
  const spaltenMinBreite = Math.max(
    spaltenMinSumme,
    bedienSumme + sichtbareSpalten.reduce((schwelle, sp) => {
      const { min, max } = COLUMN_MAX[sp.kind] ?? {};
      if (!min || !max || spaltenBudget <= 0) return schwelle;
      return Math.max(schwelle, (min * spaltenBudget) / max);
    }, 0),
  );
  // Die TATSAECHLICHE Tabellenbreite: nie unter der Rollschwelle. Liegt der
  // Behälter darunter, bleibt die Tabelle auf `spaltenMinBreite` und der
  // `TableViewport` (`overflow: auto`) rollt.
  const tabellenIst = Math.max(tabellenMax, spaltenMinBreite);

  // 🔴 **Die neue Breite (S1, §4.1): die Tabelle endet am BEHÄLTER.**
  // N8: VOR der ersten Messung ist `behaelterBreite` null, und `messBasis` fällt
  // auf 1280 zurück (`CONTENT_WIDTH.full === null`). Mit `minWidth: tabellenBreite`
  // (= 1280) in einem 920er-Behälter rollte die SEITE für mindestens einen Frame
  // waagerecht — bei 1280/1440, den Breiten des Owners. `useContainerWidth` misst
  // in einem `useEffect` NACH dem Paint. Bis dahin hängt `tabellenBreite` an
  // `tabellenIst`: der erste Frame ist zeichengleich zu heute.
  const tabellenBreite = behaelterBreite == null
    ? tabellenIst
    : Math.max(messBasis, spaltenMinBreite);
  // Was die Datenspalten HEUTE tragen — die Basis bleibt `tabellenIst`, damit die
  // fünf festen Spalten ihre PIXELBREITE behalten (Auflage §4.1).
  const datenBasis = tabellenIst > bedienSumme ? tabellenIst - bedienSumme : 0;
  // Der Überschuss, den es heute nicht gibt.
  const ueber = Math.max(0, tabellenBreite - tabellenIst);
  // 🔴 **R16-W11, Randfall benannt:** ist die elastische Spalte AUSGEBLENDET und
  // gibt es trotzdem Überschuss, nimmt `identity` nur bis 480 px und der Rest
  // hat keinen Eigentümer; der Browser verteilt ihn dann selbst, und die Summe
  // der `%`-Breiten ist < 100 %. Heute nicht erreichbar: `Description`
  // erscheint nur, wenn das Budget in den Behälter passt (`priority: 'xl'`),
  // und dann gibt es keinen Überschuss. Keine Klemme — sie würde die
  // Invariante „die Summe geht auf" (Probe B.3b) still verändern.

  // Die Pixelbreite EINER Datenspalte — vor der Verteilung auf die zwei Grenzen.
  const pxVon = (key) => {
    const max = COLUMN_MAX[SPALTE_VON[key]?.kind]?.max ?? 0;
    return spaltenBudget > 0 ? (max / spaltenBudget) * datenBasis : 0;
  };

  /** Die erklärte Breite EINER Datenspalte — als Anteil der TABELLENBREITE.
   *
   *  🔴 Zwei Zweige, eine Summe (§4.1): die fünf festen Spalten tragen
   *  `px_i / tabellenBreite`; `identity` nimmt den Überschuss bis zur Obergrenze
   *  und die elastische Spalte den Rest, den `identity` nicht aufnahm.
   *  `datenBasis = tabellenIst − bedienSumme`, NICHT `tabellenBreite − …` — sonst
   *  skalierten alle Spalten hoch (Auflage). Bei `ueber === 0` ist das Ergebnis
   *  zeichengleich zu G3b. Ohne Budget bleibt sie leer. */
  const spaltenBreite = (key) => {
    if (spaltenBudget <= 0) return {};
    const px = pxVon(key);
    // Die Identitätsspalte heißt `item`, ihre ART ist `identity` — verglichen wird
    // über die ART, nicht über den Schlüssel (sonst trifft keine Bedingung).
    const identityKey = SPALTEN.find((s) => s.kind === 'identity')?.key;
    let pxEnd = px;
    if (ueber > 0 && key === identityKey) {
      pxEnd = ELASTISCHE_SPALTE === 'identity'
        ? px + ueber
        : Math.min(px + ueber, IDENTITY_OBERGRENZE_PX);
    } else if (ueber > 0 && ELASTISCHE_SPALTE && key === ELASTISCHE_SPALTE) {
      const identityPx = pxVon(identityKey);
      pxEnd = px + (identityPx + ueber - Math.min(identityPx + ueber, IDENTITY_OBERGRENZE_PX));
    }
    return { width: `${(pxEnd / tabellenBreite) * 100}%` };
  };

  // 🔴 **Leiste und Fußzeile laufen über das GANZE Panel** — die Hausform der
  // `full`-Listen (`/products` und `/design/muster` bei 2560: Suche links,
  // Zähler/⟳ am rechten Panelrand; nur die TABELLE ist auf ihr Spaltenbudget
  // gedeckelt, Owner OE-W3 „kein Seiten-Deckel, Spaltenbudget"). Die
  // Nachbesserung 1 hatte hier einen `maxWidth` auf `tabellenIst` gesetzt
  // (Regel-16-W1); der widersprach der Hausform und ist am 2026-10-18 auf
  // Orchestrator-Entscheid zurückgenommen (Fotos `g3b2f`, 2560). `tabellenIst`
  // bleibt die Tabellenbreite — sie steuert NUR die Tabelle.

  // Das Karussell zeigt IMMER den vollen offenen Bestand, nicht den gefilterten:
  // es ist der Einstieg, nicht das Ergebnis der Suche.
  const offene = useMemo(() => offeneFaelle(zeilen ?? []), [zeilen]);

  // 🔴 **Der Schalterzustand kommt aus der Antwort** (Grill W7, 2026-10-09).
  // `SECOND_LIFE_BESCHREIBUNG_AKTIV` steht auf prod aus; `POST /<id>/beschreibung`
  // prueft ihn vor allem anderen, auch vor der Vorschau. Die Seite kann ihn nicht
  // erraten — sie muss ihn erfahren. Fail-closed: fehlt das Feld, gilt der
  // Schalter als aus (die Route sendet es seit dem 2026-10-09 immer).
  const beschreibungAktiv = daten?.beschreibung_aktiv === true;

  // 🔴 **Die Sperre des ersten Aufbaus** (N8.5): ein Fehler, solange noch NIE ein
  // Abruf gelungen ist. Danach bleiben die Bedienelemente bedienbar (N4.5), die
  // alten Zeilen stehen, und der Satz über der Liste nennt den Wiederholen-Weg.
  const sperre = fehler != null && !jeGeladen;

  // Nach einem Rabattwechsel: melden UND neu laden. Der Block zeigt den
  // gespeicherten Stand danach aus der Zeile, nicht aus einer eigenen Kopie.
  //
  // 🔴 **Die Zeile kommt seit dem 2026-10-16 mit** (`RabattBlock.onGeaendert`,
  // Luecke 2). Bis dahin trug diese Signatur nur den Text — sie war der EINZIGE
  // der acht Aufrufer des Slots, der seine Zeile nicht nennen konnte, obwohl der
  // Block sie kennt. Ohne sie stuende ein Rabattsatz ohne Gegenstand im Slot,
  // waehrend zwei Zeilen parallel laufen koennen.
  const rabattGeaendert = useCallback(async (zeile, text) => {
    zeigeHinweis(zeile, text);
    await laden();
  }, [laden, zeigeHinweis]);

  // 🔴 **Der Sortierweg des Tabellenkopfs** (N9.3, §1.1): derselbe Schlüssel kehrt
  // die Richtung um, ein NEUER beginnt bei `stock` absteigend (der Zahl wegen),
  // sonst aufsteigend. Er schreibt in die Adresse (`sort`), nicht in `useState`.
  const sortierenNach = (schluessel) => {
    if (sortKey === schluessel) set('sort', `${sortDir === 'desc' ? '' : '-'}${schluessel}`);
    else set('sort', schluessel === 'stock' ? '-stock' : schluessel);
  };

  // 🔴 **Die Blatt-Kriterien MIT WERT** (N4.6) — nicht `chips.length`: der
  // Such-Chip „Search: <Begriff>" zählt hier NICHT mit. Zwei Kriterien im Blatt
  // (Level, Shopify), je 1, wenn gesetzt.
  const blattAktiv = (stufen.length > 0 ? 1 : 0) + (zustandWerte.length > 0 ? 1 : 0);

  return (
    <PageContainer
      // 🔴 **G3b (§2.6): `width="full"`.** Bis hierher rendert die Seite OHNE
      // `width` und trägt damit das Vorgabemuster `reading` (1280) — sichtbar
      // wurde das erst ab 1920, wo die Seite die Breite nicht nutzte. Die
      // Konstante steht oben (`MUSTER`) und ist EINE Quelle für den Deckel und
      // die Meldung; der Skelett-Zweig erbt sie über denselben `PageContainer`.
      width={MUSTER}
      // 🔴 **Die Huelle friert NICHTS mehr ein** (Weg (c), 2026-10-14). Hier
      // standen vier Fesseln: die Seitenshoehe (`sx`), `height: 100%`, ein
      // `flexShrink: 0` fuer alles ausser dem Roller und ein `minHeight`-Boden
      // am Roller. Sie machten den Tabellenkasten zum EINEN Roller — und die
      // Seite rollte auf niedrigen Schirmen trotzdem, weil der Boden ihn offen
      // hielt (`Seitenroller = 692 + Boden − Hoehe`, Nullpunkt H ≈ 932).
      // Jetzt waechst die Ansicht mit ihrem Inhalt; die SEITE ist der eine
      // Roller (Messung samt Viewports an den gestrichenen Konstanten oben).
      //
      // ⚠️ `display: flex` + `flexDirection: column` BLEIBT: es stapelt die
      // Kinder ohne Randverschmelzung. Ohne feste Hoehe hat es keine
      // Schrumpfwirkung mehr — genau das ist gewollt.
      boxSx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* 🔴 **Zone 1 — Kopf** (§15-Konvention, von Hand gesetzt: kein Baustein
          stempelt `liste-kopf`; G2-Grill B2). */}
      <Box data-testid="liste-kopf">
        <ViewHeader
          title="Second Life Stock"
          subtitle="Non-A items from LSM — B, C and DEF, per item and level"
          // 🔴 **Kein `backLink` mehr (2026-10-10).** Er zeigte auf
          // `/second-life` — seit die Warteschlange stillgelegt ist, ist das die
          // EIGENE Seite. Ein Rueckweg auf sich selbst ist keiner.
          //
          // 🔴 **Der Frische-Stempel sitzt im Aktionsbereich** (Klickdummy-Nachzug,
          // Punkt 1). `rightAdornment` ist genau dafuer ausgewiesen: „Anzeigen
          // ohne Knopfcharakter". ⚠️ **Seit G3a ist die Kopfzeile AKTION-FREI**
          // (§2.2/N2.6: „Auffrischen hat genau einen Weg je Ansicht und steht am
          // Ende von Z1 — nicht im Kopf") — der `RefreshButton` sitzt in Z1.
          rightAdornment={(
            <FrischeStempel
              geholtAm={daten?.geholt_am}
              // 🔴 Regel-16-W1: faellt eine Bestandsquelle aus, sagt der Stempel
              // es (er nennt sonst einen vollstaendigen Bestandsstand).
              lsmFehler={daten?.lsm_fehler}
              bestandFehler={daten?.bestand_fehler}
            />
          )}
          actions={[]}
        />
      </Box>

      {/* 🔴 **Die EINE sammelnde Live-Region der laufenden Zeilen** (2026-10-16,
          Luecke 1, zweite Haelfte).

          Sie steht HIER und nicht in `OpenCases` — und das ist der ganze Punkt:
          `OpenCases` rendert seinen Abschnitt `Waiting for a product or a
          release` als WURZEL, jede Region darin laege also INNERHALB. Zwei
          gruene Proben zaehlen `role="status"` genau dort:
          `OpenCases.test.jsx` L.3 (`within(abschnitt)` → `toHaveLength(1)`) und
          `second-life-bestand.spec.ts` B.6 (`liste.getByRole('status')` →
          `toHaveText('2 waiting')`, das faellt an der strict-mode violation).
          Auch EINE zusaetzliche Region im Abschnitt bricht beide — es ist der
          ORT, nicht die Anzahl. Deshalb: ausserhalb des Abschnitts UND
          ausserhalb von `liste-panel` (das P8-Gegenstueck auf der Tabellenseite,
          `second-life.spec.ts` P8).

          🔴 **Warum `ProgressStatus` und nicht `ProgressButton`:** der Knopf
          traegt drei Eigenschaften, die diese Zeile nicht haben darf — sein `sx`
          wird NACH dem Aufrufer-`sx` gespreadet und ersetzt die Flaeche (die
          absolut positionierte Fuellung liefe aus dem Knopf heraus), er sperrt
          ueber natives `disabled` statt ueber das begruendete `aria-disabled`
          (`OpenCases.test.jsx` pinnt `aria-disabled="true"`), und sein
          Regionstext IST die sichtbare Beschriftung — er koennte die Zeile
          deshalb gar nicht nennen. Genau das ist hier aber die Anforderung.

          ⚠️ **Der Text kommt aus `laufendeAnsage`** und nennt die Zeile
          (`art0@B: Creating…`). Die Region steht IMMER im DOM (leer im
          Ruhezustand) — eine Region, die erst mit ihrem Text erscheint, wird von
          Sprachausgaben nicht zuverlaessig angesagt (`ProgressButton.jsx:26-28`). */}
      <ProgressStatus text={laufendeAnsage(laufend)} />

      {/* 🔴 **Der EINE Hinweis der Seite — seit der Nachbesserung 3 nur noch die
          BESTAETIGUNG eines Rabattwechsels** (R16-C1/R15-N7).

          Bis hier stand hier ein Kasten mit den letzten ZWEI Meldungen, einer
          Klemme (`slice(-2)`) und einem Zaehler fuer die herausgefallenen
          (B4-W1/C1). Der war fuer PARALLELE ABSAGEN gebaut. 🔴 **Dieser
          Erzeuger ist mit R16-C1 entfallen:** jeder Fehler — Anlegen,
          Freigeben, Kladde, Verwerfen — steht jetzt im DETAIL, an derselben
          Stelle und in derselben Form (`data-testid="fall-fehler"`,
          `role="alert"`). Ein Zwei-Slot mit Klemme und Zaehler haette fuer eine
          einzelne Bestaetigung keinen Erzeuger mehr; er ist mit seinen sechs
          Proben geloescht (Regel 15 §Rueckbau).

          ⚠️ **Warum der Hinweis HIER bleibt und nicht ins Detail wandert:** er
          hat einen Erzeuger OHNE Detail — den Rabattwechsel in der
          Bestandstabelle (`BestandZeile` → `RabattBlock`). Verdrahtet ist er an
          ZWEI Tabellenstellen (mobil `liste-karte`, Desktop `liste-tabelle`)
          PLUS im Detail (`onZumEditor`-Nachbarstelle); die zwei Tabellenwege
          sind die Begruendung, das Detail ist derselbe Kasten, weil der Satz
          dort am Ort waere, es in der Tabelle aber keinen Ort dafuer gibt. Eine
          Flaeche fuer alle drei ist die ehrlichere Form, und es ist eine
          BESTAETIGUNG, keine Absage: „nichts passiert" kann hier nicht der
          Eindruck sein.

          🔴 **Der Text nennt die ZEILE** (`art0@B: Discount saved …`), aus
          demselben Grund wie `laufend`: zwei Zeilen koennen gleichzeitig
          arbeiten, und ein Satz ohne Gegenstand ist dann nicht zuzuordnen. */}
      {hinweis && (
        <Alert
          severity="info"
          // 🔴 **Hausform fuer eine Bestaetigung** (R15-N1 / R16-O2): MUI setzt
          // ohne eigenes `role` assertiv `role="alert"`. Der Kasten traegt seit
          // R16-C1 NUR noch Bestaetigungen — `NoticeSlot` nimmt fuer alles, was
          // nicht `error` ist, `role="status"`. Dieselbe Regel hier.
          role="status"
          sx={{ mb: SP.card }}
          data-testid="hinweiskasten"
          onClose={() => setHinweis(null)}
        >
          <Typography variant="body2">
            {`${hinweis.schluessel}: ${hinweis.text}`}
          </Typography>
        </Alert>
      )}

      {/* 🔴 **Eine Bedingung, ein Leerzustand** (Regel-16-Befund W2 der Runde 3).
          Hier stand `{zeilen && …}` — und `[] && x` ergibt in JavaScript `x`,
          ein leeres Array ist WAHR. Bei `zeilen: []` standen damit „Nichts offen
          — kein Fall wartet auf Anlegen oder Freigabe." (Karussell) und „Keine
          Nicht-A-Artikel." (Tabelle) gleichzeitig untereinander.
          🔴 **Und die offene Liste BLEIBT HIER — OBERHALB der Leiste** (Owner-
          Entscheid **OE-G3-2**, §2.9): sie ist der Arbeitsvorrat und die Aussage
          der Seite. `OpenCases.jsx` wird inhaltlich **nicht** angefasst, und
          auch seine Lage nicht. Das ist eine **benannte Abweichung von N2.5/P2**
          (§13 Abweichung 1) — P2 wird auf `/second-life` NICHT gefahren. */}
      {zeilen && zeilen.length > 0 && (
        <Vorschlagsteil
          zeilen={zeilen ?? []}
          offene={offene}
          onAnlegen={(z) => annehmen(z, 'anlegen')}
          onFreigeben={(z, opts) => annehmen(z, 'freigeben', opts)}
          onAblehnen={ablehnen}
          onRabatt={rabattGeaendert}
          onKladde={stelleKladdeSicher}
          laufend={laufend}
          onZeigeBestand={() => tabelleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          onZumEditor={(z) => {
            // Erst die Tabelle in den Blick (die Zeile kann auf einer anderen
            // Seite liegen), dann oeffnet die Zeile selbst ihren Editor und
            // rollt sich in die Mitte.
            tabelleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setEditorAuftrag((a) => ({ sku: z.sku, n: (a?.n ?? 0) + 1 }));
          }}
        />
      )}

      {/* 🔴 **Die vier Hinweis-`Alert`s stehen ZWISCHEN Zone 4 und 5** (§2.2/§2.8),
          also **über** dem Daten-Panel — nicht zwischen Z3 und der Tabelle.
          🔴 **Die zwei Quellen-Hinweise tragen `role="status"` und liegen damit
          AUSSERHALB von `liste-panel`:** P8 zählt in der Liste genau EINEN
          `role="status"`-Zähler (§2.5) — und auf `/second-life` gibt es bereits
          DREI ausserhalb (`OpenCases.jsx` + diese zwei). Ohne die Trennung meldete
          P8 „4 Zähler".
          🔴 **Seit dem 2026-10-16 sind es VIER ausserhalb** — die sammelnde
          Live-Region der laufenden Zeilen (`ProgressStatus`, weiter oben) ist
          dazugekommen. Die Zählung oben bleibt richtig: sie nennt den Stand, der
          die Trennung erzwungen hat. Ohne sie meldete P8 heute „5 Zähler". */}

      {/* 🔴 Grill W7: der Schalter steht aus, also ist JEDES Speichern eine
          Absage. Ein Satz für die ganze Flaeche — je Zeile wiederholt waere er
          bei 777 Zeilen 777-mal derselbe.
          🔴 **Regel-16-W3 (Runde 1, 2026-10-14): kein Env-Name in der
          Oberflaeche.** Hier stand „the switch `SECOND_LIFE_BESCHREIBUNG_AKTIV`
          is off" — der Name einer Umgebungsvariablen ist keine Nutzerauskunft.
          `EmptyState` verbietet genau das fuer seinen `detail`-Slot
          („nie ein Endpunkt, Filter oder Env-Name", `EmptyState.jsx:60-61`); fuer
          `Alert` stand der Satz nicht, die Absicht gilt aber. POG ist eine
          INTERNE Flaeche (kein Kundentext) — deshalb ist es eine Textaenderung
          und keine Karte. Der Schalter selbst bleibt am Kode benannt — am
          `beschreibungGrund`-Kommentar in dieser Datei und in
          `api/secondLife.js` (Symbol statt Ziffer, Regel 11). */}
      {daten && !beschreibungAktiv && (
        <Alert severity="info" sx={{ mb: SP.card }}>
          Editing the description is not enabled. The column shows the state,
          changes are locked; setting the discount is not affected.
        </Alert>
      )}

      {/* 🔴 **LSM hat nicht geantwortet** (2026-10-10). Die Nebenquelle darf
          ausfallen — die Tabelle laeuft dann mit Shopify allein. Aber sie muss
          es SAGEN: ohne diesen Satz sieht ein ausgefallenes Portal aus wie ein
          Lager ohne Bestand. Der Grund steht klein darunter, damit er nicht in
          einem `title` verschwindet (ein Tooltip kommt an einem gesperrten
          Knopf nie an — dieselbe Regel wie bei den Sperrgruenden).
          🔴 `role="status"`, nicht MUIs Vorgabe `alert` — die App-Konvention
          reserviert `alert` fuer `error` (`NoticeSlot`). 🔴 **Sie sind KEIN
          Ladefehler der Liste** (§2.8): kein `[data-reason]`, kein `EmptyState`.
          Und sie nennen BEWUSST keinen naechsten Schritt: der Fehler liegt hinter
          der Schnittstelle, der einzige Weg ist ein erneuter Abruf — und der
          `RefreshButton` steht in Z1. */}
      {daten?.lsm_fehler && (
        <Alert severity="warning" role="status" sx={{ mb: SP.card }}>
          {LSM_AUSFALL_HINWEIS}
          <Typography variant="caption" sx={{ display: 'block', mt: SP.chip, overflowWrap: 'anywhere' }}>
            {daten.lsm_fehler}
          </Typography>
        </Alert>
      )}

      {/* 🔴 **Shopifys Bestand war nicht lesbar** (2026-10-11, `bestand_fehler`).
          Dasselbe Muster wie der LSM-Ausfall darueber: die Tabelle laeuft mit
          LSMs Zahl weiter und SAGT es. Bis hierher riss dieser Fehler die ganze
          Ansicht mit (prod-500 seit dem 2026-10-10). Beide Hinweise duerfen
          gleichzeitig stehen — verschiedene Quellen, verschiedene Saetze, und
          keiner nennt die andere Quelle als Lieferanten der Zahlen. */}
      {daten?.bestand_fehler && (
        <Alert severity="warning" role="status" sx={{ mb: SP.card }}>
          {BESTAND_AUSFALL_HINWEIS}
          <Typography variant="caption" sx={{ display: 'block', mt: SP.chip, overflowWrap: 'anywhere' }}>
            {daten.bestand_fehler}
          </Typography>
        </Alert>
      )}

      {/* 🔴 **Die ganz leere Bestandsspalte nennt ihren Grund** (Regel-16-W2 der
          Runde 5, getragen aus Runde 4). Die Route zaehlt je Quelle
          (`bestand_quelle`) und schreibt DIESELBE Bedingung ins Log
          (`second_life.py:1619`); bis hierher sah der Mensch sie nicht. Auf prod
          traf genau das zu — **ein** Stufenprodukt fuer 204 Artikel — und die
          Tabelle zeigte ueberall nur den Geviertstrich. Regel 22 §3 („unbekannt
          ist nie 0") und §5 („jeder Leerzustand nennt seinen Grund") gelten auch
          SPALTENWEISE, nicht nur je Zeile. Die Bedingung ist ausgelagert und rein
          geprueft (`bestandQuelleLeer`, `bestand.test.js`). */}
      {zeilen && zeilen.length > 0 && bestandQuelleLeer(daten?.bestand_quelle) && (
        <Alert severity="warning" sx={{ mb: SP.card }}>
          {LEERE_BESTANDSSPALTE_HINWEIS}
        </Alert>
      )}

      {/* 🔴 **Zone 5 — Daten-Panel** (Leiste, Tabelle, Fußzeile in EINER Fläche).
          Die Zone `liste-panel` umschliesst Z1–Z3, die Tabelle und die Fußzeile
          — und **NICHT** die offene Liste (§2.9): die liegt darüber und AUSSERHALB.
          Das ist die Voraussetzung dafür, dass P8 genau einen
          `role="status"`-Zähler **im Panel** findet (§2.5; G1a verengt `SEL.zaehler`
          auf `${zone('liste-panel')} [role="status"]`). */}
      <Panel
        pad="none"
        clip
        data-testid="liste-panel"
        // 🔴 **`overflow` ist RESPONSIV, und das ist keine Kosmetik**
        // (Nachbesserung 1, G3a). `clip` setzt `overflow: hidden` — und ein
        // Vorfahr mit `overflow ≠ visible` wird zum Bezugsrahmen fuer
        // `position: sticky` UND schluckt den waagerechten Ueberlauf. Genau
        // beides braucht die Flaeche ab `lg`: dort ist die SEITE der eine Roller
        // (`TableViewport` steht auf `overflow: visible`, `:1616`) und der
        // `thead` klebt unter der App-Leiste (`top: 64`, `:1620`).
        //
        // 🔴 **Gemessen (Regel-8-A/B des Orchestrators, 2026-10-14):** mit dem
        // unbedingten `clip` fielen ZWEI auf master gruene Bestandsproben —
        // `L.7c` („der Kopf ist aus dem Bild", `y = −2266` statt 60–70) und
        // `L.7d` (bei 1440 mit einer 2000-px-Tabelle rollte die SEITE nicht:
        // `scrollWidth = clientWidth = 1440`). Beide messen ab `lg`; unter `lg`
        // bleibt `hidden` (dort ist der `TableViewport` ohnehin der Roller und
        // schneidet selbst ab, `B.12`/`L.7f`).
        //
        // ⚠️ Auf `master` trug der `TableViewport` die Panel-Flaeche SELBST
        // (`...PANEL_SX`) — es gab keinen clipenden Vorfahren. Mit dem Panel als
        // Zone 5 (`liste-panel`, G1a-Konvention) kommt er hinzu; diese Zeile
        // nimmt ihn ab `lg` wieder heraus.
        //
        // 🔴 **Der Aufrufer gewinnt:** `Panel` spreadet `...sx` als LETZTES
        // (`Panel.jsx:105`), der responsive Wert ueberstimmt also `clip`s
        // `overflow: 'hidden'`.
        sx={{ overflow: { xs: 'hidden', lg: 'visible' } }}
      >
        <Box sx={{ px: SP.card, pt: SP.card }}>
          {/* 🔴 N8.5: der Grund der Sperre steht als SICHTBARER Text DIREKT über
              der Leiste, im selben Panel — mit der Haus-Marke
              `data-testid="erster-aufbau-gesperrt"` (`ListenMuster.jsx:562`,
              `POOverview.jsx` › `erster-aufbau-gesperrt`). `messeP9Sperre` liest genau sie
              (`helfer.ts:878`) und verlangt den Text `/locked until the list
              loads/i`. 🔴 **Kein `DisabledReason`:** dessen `@dontUseFor` schliesst
              eine durchgereichte Ursache aus. */}
          {sperre && (
            <Alert severity="warning" sx={{ mb: SP.card }} data-testid="erster-aufbau-gesperrt">
              Search and filters are locked until the list loads. Use Try again below.
            </Alert>
          )}

          <ListToolbar
            // 🔴 **Leiste und Fußzeile laufen über das GANZE Panel** — die
            // Hausform der `full`-Listen (Owner OE-W3 „kein Seiten-Deckel,
            // Spaltenbudget"): auf `/products` und `/design/muster` steht bei
            // 2560 die Suche links und der Zähler am rechten PANELRAND, während
            // nur die Tabelle an ihrem Spaltenbudget endet. G2b (`/overview`)
            // folgt dem. Die Nachbesserung 1 hatte Z1–Z3 und `liste-fuss` auf
            // `tabellenIst` gedeckelt (Regel-16-W1); das war am 2026-10-18 auf
            // Orchestrator-Entscheid zurückgenommen (Fotos `g3b2f`, 2560).
            // `ListToolbar` deckelt weiterhin NUR das Suchfeld (`searchMax`,
            // `ListToolbar.jsx:275`).
            // 🔴 Der Kurzbefehl ist Einzelinstanz (G0.3) — die Leiste verwaltet
            // die Warteliste selbst.
            shortcut="/"
            search={{
              value: q,
              onChange: (v) => set('q', v),
              onCommit: (v) => set('q', v),
              // 🔴 Ein Text in BEIDEN Breiten (N7.4). Der zugängliche Name beginnt
              // mit „Search" (P5); `inputRole` gibt dem Feld die Rolle `searchbox`
              // (P5/P16/P23/P34 suchen genau die, `selektoren.js` §`suchfeld`).
              placeholder: 'Search item no. or name…',
              label: 'Search items',
              inputRole: 'searchbox',
              // N8.5: die Sperre des ersten Aufbaus reist über `{...search}`.
              disabled: sperre,
              // 🔴 N2.9/N7.3: **die Suche füllt den Rest von Z1** — das `sx` geht
              // an das `TextField`-Wurzelelement (`MuiFormControl-root`).
              sx: { flex: 1, minWidth: 0 },
            }}
            // 🔴 Genau ZWEI Facetten (die Leiste wirft bei > 3): Level und
            // Shopify, je multi, Desktop als Pillen (`desktopForm="pill"`, BS7).
            facets={[
              <BottomSheetMultiSelect
                key="level"
                desktopForm="pill"
                label="Level"
                value={stufen}
                onChange={(v) => set('level', v)}
                options={STUFEN.map((s) => ({ value: s, label: s }))}
                disabled={sperre}
              />,
              <BottomSheetMultiSelect
                key="shopify"
                desktopForm="pill"
                label="Shopify"
                value={zustandWerte}
                onChange={(v) => set('shopify', v)}
                options={ZUSTAENDE.map((s) => ({ value: s, label: ZUSTAND_LABEL[s] || s }))}
                disabled={sperre}
              />,
            ]}
            moreFilters={{
              disabled: sperre,
              // 🔴 Dieselben zwei Kriterien wie die Z1-Facetten (§2.3) — ohne den
              // Eintrag wären sie mobil UNERREICHBAR (die Leiste rendert Facetten
              // nie inline unter `sm`, N11.8), ein stiller Verlust. Der Leerwert
              // heisst „Any" (N11.14). 🔴 **Kein `count` je Option** (§4/§13
              // Abweichung 11): `BottomSheetMultiSelect` kennt keins.
              criteria: [
                {
                  key: 'level',
                  kind: 'select',
                  label: 'Level',
                  multiple: true,
                  value: stufen,
                  onChange: (v) => set('level', v),
                  options: STUFEN.map((s) => ({ value: s, label: s })),
                  emptyLabel: 'Any',
                },
                {
                  key: 'shopify',
                  kind: 'select',
                  label: 'Shopify',
                  multiple: true,
                  value: zustandWerte,
                  onChange: (v) => set('shopify', v),
                  options: ZUSTAENDE.map((s) => ({ value: s, label: ZUSTAND_LABEL[s] || s })),
                  emptyLabel: 'Any',
                },
              ],
              // 🔴 Der Sortier-Abschnitt ist der ERSTE des Blattes (N11.14/P17):
              // die Liste ist sortierbar, und mobil gibt es keinen Tabellenkopf.
              sort: {
                value: sortFeld,
                onChange: (v) => set('sort', v),
                options: [
                  { value: '-stock', label: 'Stock (high to low)' },
                  { value: 'stock', label: 'Stock (low to high)' },
                  { value: 'name', label: 'Item (A–Z)' },
                  { value: '-name', label: 'Item (Z–A)' },
                ],
              },
              // 🔴 **Der Blatt-Zaehler kennt „unbekannt"** (Regel-16-B1, N1):
              // `FilterSheet` liest `null` als „noch nicht bekannt" und schreibt
              // dann „Show results" OHNE Zahl (`FilterSheet.jsx:159-164`). Eine
              // `0` dort liest sich als „nichts da" — genau der Satz, den die
              // Flaeche in der Ausfall-Lage nicht fuehren kann.
              resultCount: jeGeladen && !bestandUnbekannt ? sichtbar.length : null,
              // 🔴 `activeCount` zählt die BLATT-Kriterien mit Wert (N4.6) — NICHT
              // `chips.length` (der Such-Chip zählt nicht mit, `ListToolbar` §`moreFilters`).
              activeCount: blattAktiv,
              onClearAll: clearAll,
            }}
            // 🔴 Z2 — EINE Quick-Filter-Pille „In stock", `alwaysShow` (Pflicht:
            // ohne sie ist Z2 am Desktop leer, `FilterPillRow.jsx:161`).
            // 🔴 **Ihr Zähler ist der BESTAND** (OE-G3-1, §4: „Zahl = Anzahl im
            // Bestand"), nicht die gefilterte Menge — und er steht auch bei einer
            // gemessenen 0 (`mitBestand` zählt `null` nicht als Bestand).
            quickFilters={(
              <FilterPillRow
                alwaysShow
                multi
                value={state.quick ?? LEER_LISTE}
                onChange={(v) => set('quick', v)}
                disabled={sperre}
                options={[{
                  value: 'stock',
                  label: 'In stock',
                  // 🔴 **Die Zahl fehlt in ZWEI Lagen** (Regel-16-B1): solange
                  // nie geladen (`zeilen` ist `null`, `mitBestand(null ?? [])`
                  // waere eine gemessene 0 — der Zaehler daneben schuetzt sich
                  // genau so ueber `jeGeladen`) und wenn die Bestandsquelle
                  // ausgefallen ist und die Liste leer bleibt. `count: undefined`
                  // laesst den Zaehler am Chip ganz weg (`FilterPillRow.jsx:287`).
                  count: jeGeladen && !bestandUnbekannt ? mitBestand(zeilen ?? []) : undefined,
                }]}
              />
            )}
            // 🔴 §4.2/N4.3: GENAU EIN Zähler, „N of M items" — er sitzt am Rechner
            // in Z1 und mobil in Z3 (die Leiste rendert ihn in genau einem).
            // 🔴 **Kein Zähler, solange nie geladen** (§8.2: er springt nicht auf
            // 0); im Fehler des ersten Aufbaus spricht der Leerzustand.
            // 🔴 **Und kein Zähler, wenn die Bestandsquelle ausgefallen ist und
            // die Liste leer bleibt** (Regel-16-B1): `0 items` waere eine
            // gemessene Null ueber eine Quelle, die nicht geantwortet hat —
            // `ResultCount` fuehrt „eine 0 liest sich als ‚nichts da'"
            // ausdruecklich in seinem `@dontUseFor` (`ResultCount.jsx:54`).
            count={jeGeladen && !bestandUnbekannt
              ? { noun: 'items', n: sichtbar.length, m: zeilen ? zeilen.length : 0 }
              : undefined}
            // 🔴 N2.6: **Auffrischen hat genau einen Weg je Ansicht und steht am
            // Ende von Z1.** `ariaLabel="Refresh"` hält den Namen, den die
            // Bestands-Spec greift (§5.2 Ziff. 3).
            refresh={(
              <RefreshButton
                ariaLabel="Refresh"
                onClick={laden}
                firstLoad={!jeGeladen && fehler == null}
              />
            )}
            // 🔴 Z3 — die Chips und „Clear all". Die offene Liste filtert NICHT
            // mit (sie zeigt immer den vollen offenen Bestand).
            query={{ chips, clearAll }}
          >
            {/* 🔴 N10.4/P13 — ein UNBEKANNTER Wert in der Adresse wird VERWORFEN
                und EINMAL gemeldet. Ohne diese Zeile fiele die Bereinigung still
                aus. */}
            {ignoredValues.length > 0 && (
              <Alert severity="info" data-testid="ignoriert-hinweis" sx={{ mt: SP.card }}>
                {`Ignored unknown filter value “${ignoredValues[0].value}”.`}
              </Alert>
            )}

            {/* 🔴 **Fehler NACH einer Änderung** (N4.5, §2.8): die Bedienelemente
                behalten ihren Wert und bleiben bedienbar, die alten Zeilen stehen.
                🔴 **Nur, wenn Zeilen sichtbar sind** — sonst stünde der Satz ohne
                Gegenstand. Der Fehler des ERSTEN Aufbaus hat einen eigenen Ort
                (die Sperre oben + der `error`-Leerzustand unten). */}
            {fehler && jeGeladen && sichtbar.length > 0 && (
              <Alert severity="warning" data-testid="aktualisierung-gescheitert" sx={{ mt: SP.card }}>
                Could not refresh the list. The last loaded rows are still shown. Use Refresh to try again.
              </Alert>
            )}


            {/* 🔴 **Das Skelett** (§2.6: „der Skelett trägt das Muster der SEITE").
                🔴 **Seit G3b zieht es mit — und zwar OHNE eigene Zeile:** der
                Skelett-Zweig liegt im SELBEN `PageContainer` wie die Tabelle und
                erbt `width="full"` damit von selbst. Bei `/overview` war das eine
                eigene Datei (`POOverviewSkeleton.jsx`) mit eigenem `MUSTER`-Wert,
                den PW14 vergleicht; hier wäre eine zweite Konstante eine zweite
                Quelle für dieselbe Aussage. Und die Spaltenzahl kommt aus
                DERSELBEN Quelle wie Kopf und Rumpf (`sichtbar`), nicht aus einer
                Acht.
                🔴 **Der Skelett-Kasten MISST jetzt selbst** (Regel-16-Warnung W2,
                Nachbesserung 1): `ref={tabelleRef}` hing bis hierher nur am
                geladenen Kasten. Im Ladezustand war `behaelterBreite` deshalb
                `null`, `messBasis` fiel auf das Muster (1280) zurück, und das
                Skelett zeigte unter ≈ 1600 px mindestens eine Datenspalte MEHR
                als die Tabelle, die danach kam. Mit dem `ref` läuft dieselbe
                Messung in beiden Zuständen, und die Spaltenwahl ist dieselbe.
                🔴 **Mobil ist auch das Skelett eine Karte** — ein Tabellen-Skelett
                auf dem Telefon kündigte einen Baum an, den es dort nicht gibt. */}
            {zeilen === null && !fehler && (
              mobil ? (
                <Box data-testid="liste-karte" sx={{ mt: SP.card }}>
                  <Stack spacing={SP.card}>
                    {[0, 1, 2].map((i) => (
                      <Skeleton key={i} variant="rectangular" height={120} sx={{ borderRadius: PANEL_RADIUS }} />
                    ))}
                  </Stack>
                </Box>
              ) : (
              <Box ref={tabelleRef} data-testid="liste-tabelle" sx={{ mt: SP.card }}>
                <TableViewport
                  footerPadPx={0}
                  sx={{ maxWidth: tabellenBreite > 0 ? tabellenBreite : undefined }}
                >
                  <Table
                    size="small"
                    aria-label="Second Life Stock"
                    sx={{ tableLayout: 'fixed', minWidth: tabellenBreite }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: BILD_SPALTE_PX, px: SP.tight }}>&nbsp;</TableCell>
                        {sichtbareSpalten.map((sp) => (
                          <TableCell key={sp.key} sx={spaltenBreite(sp.key)}>&nbsp;</TableCell>
                        ))}
                        <TableCell sx={{ width: BEDIENBREITE.aktion }}>&nbsp;</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <ZeilenSkelett anzahl={1 + sichtbareSpalten.length + 1} />
                      <ZeilenSkelett anzahl={1 + sichtbareSpalten.length + 1} />
                      <ZeilenSkelett anzahl={1 + sichtbareSpalten.length + 1} />
                    </TableBody>
                  </Table>
                </TableViewport>
              </Box>
              )
            )}

            {/* 🔴 **Fehler des ERSTEN Aufbaus** (§2.8): `EmptyState reason="error"`
                ERSETZT den früheren `Alert` — mit „Try again" (`onRetry`). Suche,
                beide Facetten, die Quick-Pille und der Filter-Öffner sind gesperrt
                (N8.5), der Grund steht als `erster-aufbau-gesperrt` über der
                Leiste. 🔴 **Ersetzt, nicht ergänzt** — der alte Kommentar
                begründete die Alert-Fassung mit dem Nebeneinander zweier Sätze,
                das mit dem Ersetzen entfällt. */}
            {sperre && (
              <EmptyState reason="error" message={fehler} onRetry={laden} />
            )}

            {/* 🔴 **Keine Daten** (§2.8): der `detail`-Satz nennt bereits WANN
                welche entstehen.
                🔴 **Und wenn die QUELLE ausgefallen ist, ist der Grund ein
                anderer** (Regel-16-B1): `reason="unknown"`, nicht `no-data` —
                Regel 22 §5 fuehrt genau diese Lage („eine Quelle hat nicht
                geliefert — die leere Liste bedeutet nichts"), und die Aussage
                „No non-A items." waere hier eine Behauptung, die die Flaeche
                nicht fuehren kann. Hausform: `ListenMuster.jsx:1372-1379`
                (`unknown` mit `message` UND `detail`). */}
            {zeilen && zeilen.length === 0 && !fehler && (
              bestandUnbekannt ? (
                <EmptyState
                  reason="unknown"
                  message="No data from LSM."
                  detail="An empty list here does not mean there are no B, C or DEF items."
                />
              ) : (
                <EmptyState
                  reason="no-data"
                  message="No non-A items."
                  detail="A row appears as soon as LSM carries a B, C or DEF level with stock."
                />
              )
            )}

            {/* 🔴 **Kein Treffer — ZWEI Wortlaute** (§2.8/N8.2). Reine Suche: der
                Begriff steht IM Satz (`query`), mit „Clear search". Filter/
                Vorgaben: der Filter-Satz mit „Clear all filters".
                🔴 **Der Begriff-Echo-Satz NUR bei reiner Suche** — sonst wäre er
                identisch mit der Filter-Variante, und beide wären grün, wenn nur
                einer existiert (§5.5 Ziff. 4). */}
            {zeilen && zeilen.length > 0 && sichtbar.length === 0 && (
              q.trim() ? (
                <EmptyState
                  reason="no-match"
                  query={q.trim()}
                  noun="item"
                  searchedIn="Searched in: item number and name."
                  onClearSearch={() => set('q', null)}
                />
              ) : (
                <EmptyState
                  reason="no-match"
                  message="No match for the current filters."
                  onClearFilters={clearAll}
                />
              )
            )}

            {sichtbar.length > 0 && (
              // 🔴 **Die zwei Zonen sind GEGENSTÜCKE** (§2.1 5d): unter `md` trägt
              // die Seite `liste-karte`, darüber `liste-tabelle` — nie beide. Das
              // ist die Voraussetzung dafür, dass `messeP20`/`messeP22` mobil
              // Karten zählen (`istSchmal`, `helfer.ts:496`) und die Zonen-Probe
              // P3 eine lückenlose Reihenfolge sieht.
              mobil ? (
              // 🔴 Die Marke `liste-karte` stempelt KEIN geteilter Baustein
              // (§5.3/B2) — die Ansicht setzt sie von Hand, wie
              // `liste-panel`/`liste-tabelle`/`liste-fuss` daneben. P15/P20/P22
              // greifen über sie zu (`SEL.karte`).
              <Box data-testid="liste-karte" sx={{ mt: SP.card }}>
                <Stack spacing={SP.card}>
                  {seite.map((z) => (
                    <BestandZeile
                      key={z.sku}
                      zeile={z}
                      onGespeichert={laden}
                      onRabatt={rabattGeaendert}
                      beschreibungAktiv={beschreibungAktiv}
                      mobil
                      sichtbar={sichtbarSpalte}
                      spaltenBreite={spaltenBreite}
                      editorAuftrag={editorAuftrag}
                    />
                  ))}
                </Stack>
              </Box>
              ) : (
              // 🔴 `liste-tabelle` ist §15-Konvention und wird von KEINEM Baustein
              // gestempelt (G2-Grill B2) — sie wird von Hand gesetzt.
              //
              // 🔴 G0b.5: der Kasten misst die **Container-Breite** (BW4,
              // `useContainerWidth`) — die rohe Innenbreite, aus der der Kanal
              // `min(Behälter, Budget)` bildet. Sein `ref` sitzt hier und nicht am
              // `TableViewport`: gemessen wird der RAUM, nicht die gedeckelte
              // Tabelle (sonst mässe sich der Deckel selbst).
              <Box ref={tabelleRef} data-testid="liste-tabelle" sx={{ mt: SP.card }}>
                {/* 🔴 `maxHeight: 'calc(100vh - 300px)'` ist WEG (2026-10-12). Die
                    Zahl war eine Schaetzung der Huelle ueber der Tabelle; sie traf
                    sie nicht, also ragte der Kasten unter den Bildrand und die
                    SEITE rollte ein zweites Mal.
                    🔴 **Und seit Weg (c) (2026-10-14) gibt es hier gar keine Hoehe
                    mehr:** der `TableViewport` waechst mit seinem Inhalt, die SEITE
                    ist der eine Roller. Er BLEIBT trotzdem — er traegt den
                    waagerechten Roller unter `lg` (B.12 pinnt ihn) und den
                    deckenden Klebekopf.
                    🔴 **Der `sx` hier ist die Stelle, an der der Aufrufer gewinnt —
                    und der Merge ist FLACH** (`TableViewport.jsx` spreadet `...(sx
                    || {})` als LETZTES): `'& .MuiTableCell-stickyHeader'` nimmt
                    `backgroundColor … !important` und `zIndex: 6` mit, wenn er sie
                    nicht wiederholt — die Regel steht deshalb VOLLSTÄNDIG.
                    🔴 **`overflow`/`top` sind responsiv:** unter `lg` bleibt `auto`
                    (der Behälter ist ein Scroll-Container ohne Höhe, der Kopf klebt
                    nicht), ab `lg` wird die SEITE der Roller und der `thead` klebt
                    unter der App-Leiste (`top: 64`).
                    ⚠️ **Ohne `PANEL_SX`:** die sichtbare Fläche ist jetzt das
                    `Panel` (§2.2/§2.6) — der Viewport trägt nur noch Roller und
                    Klebekopf. */}
                <TableViewport
                  footerPadPx={0}
                  // 🔴 Die MELDUNG (BW6): `priorities` schaltet den Kanal ein (und
                  // damit `SEARCH_MAX` als Suchdeckel — **kein eigenes
                  // `searchMax`**, §2.6/W10), die rohe Behälterbreite reist als
                  // `containerWidth` mit.
                  priorities={SPALTEN}
                  containerWidth={behaelterBreite}
                  // 🔴 N9.6 — beide zusammen: die **BILDSPALTE** bleibt beim
                  // Rollen im Bild (`stickyFirstColumn`), und der weiche Rand
                  // sagt, dass rechts Spalten weitergehen (`edgeHint`). Die linke
                  // Hälfte des Randes schaltet `stickyFirstColumn` ab — die
                  // klebende Spalte ist der linke Anker.
                  //
                  // 🔴 **Die klebende Spalte ist die BILDSPALTE, nicht „Item".**
                  // `stickyFirstColumn` nimmt die ERSTE Spalte der Tabelle
                  // (`TableViewport.jsx:363`, `th:first-of-type`); hier ist das
                  // die 56-px-Bildspalte. 🔴 **Das ist eine benannte Abweichung
                  // von Plan §2.6**, der „die Kennung — klebt" sagt: `TableViewport`
                  // kennt nur „erste Spalte", und ein zweiter klebender Anker wäre
                  // eine Baustein-Änderung (der parallele Zweig G5b baut dort).
                  // Die Bildspalte ist die Wiedererkennung und trägt dieselbe
                  // Rolle, die §2.6 ihr mit `kern` gibt — sie stehen zu lassen ist
                  // die kleinere Abweichung. Changelog, Feature-Doc und die
                  // `SPALTEN`-Tabelle oben sagen seither dasselbe.
                  //
                  // 🔴 **Der Kantenhinweis hat erst seit der Rollschwelle einen
                  // Gegenstand:** bis zur Nachbesserung 1 war die Tabelle immer
                  // `min(Behälter, Budget)`, also nie breiter als ihr Kasten —
                  // `edgeHint` konnte höchstens über gequetschten Inhalt
                  // anschlagen. Jetzt rollt sie zwischen `md` (900 px Viewport)
                  // und der Schwelle (≈ 1067 px) wirklich.
                  stickyFirstColumn
                  edgeHint
                  // 🔴 Das ANWENDEN des DECKELS = min(Behälter, Budget)
                  // (§4.1:568) — der Verbraucher deckelt seinen Kasten damit; der
                  // Baustein meldet nur (PW4/PW4b). Die Tabelle selbst trägt
                  // `minWidth: spaltenMinBreite` und rollt darunter im Kasten.
                  sx={{
                    maxWidth: tabellenBreite > 0 ? tabellenBreite : undefined,
                    overflow: { xs: 'auto', lg: 'visible' },
                    '& .MuiTableCell-stickyHeader': {
                      backgroundColor: (t) => `${t.palette.background.paper} !important`,
                      zIndex: 6,
                      top: { xs: 0, lg: 64 },
                    },
                  }}
                >
                  <Table
                    size="small"
                    stickyHeader
                    aria-label="Second Life Stock"
                    // 🔴 N9.5: **Spalten springen nicht** — `tableLayout: 'fixed'`
                    // mit erklärten Breiten für die Kernspalten. Ohne die feste
                    // Satzart rechnet der Browser die Spaltenbreiten aus ALLEN
                    // Zeilen; im Leerzustand fehlen die Datenzeilen, die Spalten
                    // ziehen sich auf die Kopfbreite zusammen, und P33 mässe genau
                    // diesen Versatz.
                    sx={{ tableLayout: 'fixed', minWidth: tabellenBreite }}
                  >
                    <TableHead>
                      <TableRow>
                        {/* 🔴 Die Breiten stehen an den KOPFZELLEN:
                            `table-layout: fixed` nimmt sie aus der ersten Zeile
                            (N9.5). Bild und ⋮ sind FEST (56 / 140), die sechs
                            Datenspalten tragen ihren Anteil am Budget. */}
                        <TableCell sx={{ width: BILD_SPALTE_PX, px: SP.tight }}>Image</TableCell>
                        {/* 🔴 **Eine Quelle für Kopf UND Rumpf** (`sichtbarSpalte`):
                            sonst zeigte der Kopf eine Spalte an, die die Zeilen
                            nicht füllen — oder umgekehrt. */}
                        {sichtbarSpalte.has('item') && (
                        <TableCell
                          sortDirection={sortKey === 'name' ? sortDir : false}
                          sx={spaltenBreite('item')}
                        >
                          <TableSortLabel
                            active={sortKey === 'name'}
                            direction={sortKey === 'name' ? sortDir : 'asc'}
                            onClick={() => sortierenNach('name')}
                          >
                            Item
                          </TableSortLabel>
                        </TableCell>
                        )}
                        {sichtbarSpalte.has('level') && (
                        <TableCell sx={spaltenBreite('level')}>Level</TableCell>
                        )}
                        {sichtbarSpalte.has('stock') && (
                        <TableCell
                          align="right"
                          sortDirection={sortKey === 'stock' ? sortDir : false}
                          sx={spaltenBreite('stock')}
                        >
                          <TableSortLabel
                            active={sortKey === 'stock'}
                            direction={sortKey === 'stock' ? sortDir : 'desc'}
                            onClick={() => sortierenNach('stock')}
                          >
                            Stock
                          </TableSortLabel>
                        </TableCell>
                        )}
                        {sichtbarSpalte.has('shopify') && (
                        <TableCell sx={spaltenBreite('shopify')}>Shopify</TableCell>
                        )}
                        {sichtbarSpalte.has('price') && (
                        <TableCell align="right" sx={spaltenBreite('price')}>Price</TableCell>
                        )}
                        {sichtbarSpalte.has('beschreibung') && (
                        <TableCell sx={spaltenBreite('beschreibung')}>Description</TableCell>
                        )}
                        {/* 🔴 Die ⋮-Spalte trägt `BEDIENBREITE.aktion` (140, §2.6). */}
                        <TableCell align="right" sx={{ width: BEDIENBREITE.aktion }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {seite.map((z) => (
                        <BestandZeile
                          key={z.sku}
                          zeile={z}
                          onGespeichert={laden}
                          onRabatt={rabattGeaendert}
                          beschreibungAktiv={beschreibungAktiv}
                          sichtbar={sichtbarSpalte}
                          spaltenBreite={spaltenBreite}
                          editorAuftrag={editorAuftrag}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </TableViewport>
              </Box>
              )
            )}

            {/* 🔴 Die Anzeige-Seitenweise (Regel-16-W3). `count` ist die GEFILTERTE
                Zahl, nicht die geladene: geblaettert wird ueber das, was der Mensch
                gerade sieht.
                🔴 **Und die Leiste erscheint erst, wenn es eine zweite Seite gibt**
                (2026-10-11, Owner: „Tabellenfooter ist leer, kann dann auch weg").
                `TableFooterSimple` rendert seinen Rahmen bei jedem `count > 0`; den
                Blaetterer darin aber erst ueber `rowsPerPage`. Ohne `leftSlot` — und
                diesen Aufrufer uebergibt keinen — stand bei 22 Zeilen und 50 je Seite
                damit ein **leerer 44-px-Streifen mit Oberkante** unter der Tabelle.
                🔴 **Owner-Entscheid OE-G3-1:** die Fußzeile zeigt den
                **Seitenbereich**, NICHT den Bestand — „Z in stock" steht am
                Quick-Filter (§2.6/§13 Abweichung 10).
                🔴 **`stickyOnMobile={false}`** (BS17, §2.1): die Vorgabe des
                Bausteins ist `true` — „ohne Prop" wäre der GEGENTEIL-Fall des
                Opt-ins.
                🔴 **`liste-fuss` nur mit Inhalt** (N8.7, Hausform `ProductsTable`):
                die Zone existiert nicht ODER hat Textinhalt. */}
            {sichtbar.length > ROWS_PER_PAGE && (
              <Box data-testid="liste-fuss">
                <TableFooterSimple
                  count={sichtbar.length}
                  page={seiteAktiv}
                  rowsPerPage={ROWS_PER_PAGE}
                  // 🔴 MUI zählt die Seite 0-basiert, die Adresse 1-basiert (§3.1).
                  onPageChange={(p) => set('page', p + 1)}
                  maxWidth="100%"
                  stickyOnMobile={false}
                />
              </Box>
            )}
          </ListToolbar>
        </Box>
      </Panel>


      {/* 🔴 **Kein „Open the queue"-Link mehr (2026-10-10).** Er zeigte auf
          `/second-life` — seit die Warteschlange stillgelegt ist, ist das die
          EIGENE Seite, und den Ort, den der Text nennt, gibt es nicht mehr. */}

      {/* Ablehnen ist nicht umkehrbar von hier aus — es fragt einmal nach. */}
      <Dialog open={verwerfen != null} onClose={verwerfenAbbrechen}>
        <DialogTitle>Discard case?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {verwerfen?.sku} is filed as scrap. This is the same operation
            as in the queue and sets the origin to “Scrap”.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={verwerfenAbbrechen}>Cancel</Button>
          <Button color="error" onClick={verwerfenBestaetigt}>Discard</Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
}
